import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import './App.css';

/* ─── DİYET FİLTRELERİ ──────────────────────────────────────── */
const DIETARY_FILTERS = [
  { id:'lactose_free', icon:'🥛', label:'Laktozsuz' },
  { id:'gluten_free',  icon:'🌾', label:'Glütensiz' },
  { id:'vegetarian',   icon:'🥬', label:'Vejetaryen' },
  { id:'vegan',        icon:'🌱', label:'Vegan' },
  { id:'no_pork',      icon:'🚫', label:'Domuz eti yok' },
  { id:'no_sugar',     icon:'🍬', label:'Şekersiz' },
];

/* ─── SU TAKİBİ SABİTLERİ ───────────────────────────────────── */
const WATER_DAYS_SHORT = ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'];
function getWaterKey(date) { return `water_${date.toISOString().split('T')[0]}`; }
function getWeekWater() {
  const result = [];
  const today = new Date();
  const dow = (today.getDay() + 6) % 7; // Pazartesi=0
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - dow + i);
    const key = getWaterKey(d);
    try { result.push(parseInt(localStorage.getItem(key) || '0', 10)); }
    catch { result.push(0); }
  }
  return result;
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Tüm günlerin yemek günlüğündeki toplam kayıt sayısı (rozetler için).
function getFoodLogCount() {
  let c = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('diyet_foodlog_')) c += (JSON.parse(localStorage.getItem(k) || '[]')).length;
    }
  } catch {}
  return c;
}

// #17 — Bağlamsal akıllı nudge: o anki duruma göre tek bir kısa öneri.
function getHomeNudge({ foodCount, waterCount, waterTarget, eatenKcal, targetKcal, lastWeightDays }) {
  const h = new Date().getHours();
  if (foodCount === 0 && h >= 11)
    return { id: 'log_food', text: '🍽️ Bugün henüz bir şey eklemedin — öğününü günlüğe eklemeyi unutma.' };
  if (waterTarget && waterCount < waterTarget / 2 && h >= 14)
    return { id: 'water', text: '💧 Su hedefinin yarısının altındasın. Bir bardak su iyi gelir!' };
  if (targetKcal && eatenKcal < targetKcal * 0.5 && h >= 20)
    return { id: 'low_intake', text: '🌙 Bugün hedefinin oldukça altındasın — akşam öğününü atlama.' };
  if (lastWeightDays != null && lastWeightDays >= 7)
    return { id: 'weigh', text: '⚖️ Bir haftadır kilo girmedin. Trendini güncel tutmak için tart.' };
  return null;
}

// Başarı rozetleri — mevcut verilerden hesaplanır.
function computeBadges({ plansCount, streak, foodCount, weightCount, waterMet }) {
  return [
    { id: 'first_plan', ic: '🌱', title: 'İlk Adım',    desc: 'İlk planını oluşturdun',        earned: plansCount >= 1 },
    { id: 'planner',    ic: '📅', title: 'Planlı',      desc: '3 plan oluştur',                earned: plansCount >= 3 },
    { id: 'streak3',    ic: '🔥', title: 'Seri Başladı', desc: '3 gün üst üste',               earned: streak >= 3 },
    { id: 'streak7',    ic: '⚡', title: 'Haftalık',     desc: '7 gün seri',                    earned: streak >= 7 },
    { id: 'logger',     ic: '🍽️', title: 'Kayıt Tutan', desc: '10 yemek kaydı',               earned: foodCount >= 10 },
    { id: 'logger50',   ic: '📓', title: 'Disiplinli',   desc: '50 yemek kaydı',               earned: foodCount >= 50 },
    { id: 'tracker',    ic: '⚖️', title: 'Takipçi',      desc: '5 kilo kaydı',                  earned: weightCount >= 5 },
    { id: 'hydrated',   ic: '💧', title: 'Susuz Kalma',  desc: 'Günlük su hedefini tuttur',     earned: !!waterMet },
  ];
}

// ── Adaptif hedefleme (MacroFactor tarzı) ──
// Kilo trendini lineer regresyonla analiz edip hedefe göre durum + öneri üretir.
function readWeightLog() {
  try { return JSON.parse(localStorage.getItem('diyet_weight_log') || '[]'); } catch { return []; }
}
function analyzeWeightTrend(log, goal, targetCalories) {
  const pts = (log || [])
    .filter(w => w?.dateISO && Number.isFinite(Number(w.weight)))
    .map(w => ({ t: new Date(w.dateISO + 'T12:00:00').getTime(), w: Number(w.weight) }))
    .sort((a, b) => a.t - b.t);
  if (pts.length < 3) return { status: 'insufficient', count: pts.length, daysSpan: 0 };

  // Son 28 güne odaklan (yeterliyse), değilse tüm veri.
  const lastT = pts[pts.length - 1].t;
  const recent = pts.filter(p => lastT - p.t <= 28 * 86400000);
  const use = recent.length >= 3 ? recent : pts;
  const daysSpan = Math.round((use[use.length - 1].t - use[0].t) / 86400000);
  if (daysSpan < 10) return { status: 'insufficient', count: pts.length, daysSpan };

  // Lineer regresyon (x = ilk günden bu yana geçen gün).
  const x0 = use[0].t;
  const xs = use.map(p => (p.t - x0) / 86400000);
  const ys = use.map(p => p.w);
  const n = xs.length;
  const sx = xs.reduce((a, b) => a + b, 0), sy = ys.reduce((a, b) => a + b, 0);
  const sxx = xs.reduce((a, b) => a + b * b, 0);
  const sxy = xs.reduce((a, b, i) => a + b * ys[i], 0);
  const denom = n * sxx - sx * sx;
  const slope = denom ? (n * sxy - sx * sy) / denom : 0;     // kg/gün
  const ratePerWeek = Math.round(slope * 7 * 100) / 100;
  const bodyweight = ys[ys.length - 1];
  const weeklyPct = bodyweight ? (ratePerWeek / bodyweight) * 100 : 0;

  const isLoss = /lose|cut/.test(goal || '');
  const isGain = /gain|bulk/.test(goal || '');
  const base = targetCalories || 0;
  const step = base ? Math.max(100, Math.round(base * 0.08 / 10) * 10) : 150; // ~%8

  let status = 'on_track', message = '', suggestedDelta = 0;
  const abs = Math.abs(ratePerWeek);

  if (isLoss) {
    if (ratePerWeek > -0.05)        { status = 'plateau';  message = `Son ${daysSpan} gündür kilon neredeyse sabit. Metabolizman uyum sağlamış olabilir.`; suggestedDelta = base ? -step : 0; }
    else if (weeklyPct < -1.2)      { status = 'too_fast'; message = `Haftada ${abs} kg veriyorsun — bu çok hızlı, kas kaybı riski var.`; suggestedDelta = 120; }
    else                            { status = 'on_track'; message = `Haftada ${abs} kg veriyorsun — tam hedef aralığında. Böyle devam!`; }
  } else if (isGain) {
    if (ratePerWeek < 0.05)         { status = 'plateau';  message = `Son ${daysSpan} gündür kilon artmıyor. Kalori fazlan yetersiz olabilir.`; suggestedDelta = base ? step : 0; }
    else if (weeklyPct > 0.6)       { status = 'too_fast'; message = `Haftada +${abs} kg — fazlası yağ olarak depolanabilir.`; suggestedDelta = -120; }
    else                            { status = 'on_track'; message = `Haftada +${abs} kg — kontrollü kütle artışı. Harika!`; }
  } else {
    if (abs <= 0.15)                { status = 'on_track'; message = 'Kilonu başarıyla koruyorsun.'; }
    else                            { status = 'drifting'; message = `Kilon haftada ${ratePerWeek > 0 ? '+' : ''}${ratePerWeek} kg değişiyor.`; suggestedDelta = ratePerWeek > 0 ? -120 : 120; }
  }

  const suggestedTarget = (suggestedDelta && base) ? base + suggestedDelta : null;
  return { status, ratePerWeek, weeklyPct: Math.round(weeklyPct * 10) / 10,
    count: pts.length, daysSpan, message, suggestedDelta, suggestedTarget, bodyweight };
}

// Google Identity Services script'ini bir kez yükle.
let _gsiPromise = null;
function loadGoogleScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (_gsiPromise) return _gsiPromise;
  _gsiPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { _gsiPromise = null; reject(new Error('Google yüklenemedi')); };
    document.head.appendChild(s);
  });
  return _gsiPromise;
}

/* ─── SABİTLER ──────────────────────────────────────────────── */
const MARKETS = [
  { id:'BİM',         color:'#f59e0b', yakinda:true },
  { id:'A101',        color:'#ef4444' },
  { id:'Migros',      color:'#f97316' },
  { id:'Şok',         color:'#3b82f6' },
  { id:'MacroCenter', color:'#22c55e' },
  { id:'File',        color:'#a855f7', yakinda:true },
];

const GOAL_LABELS = {
  cut:'🔥 Cut', bulk:'🏋️ Bulk', aggressive_cut:'🔥🔥 Aggressive Cut',
  maintain_sport:'⚡ Kilonu koru', maintain_no_sport:'⚡ Kilonu koru',
  lose_weight_sport:'🏃 Kilo ver', lose_weight_no_sport:'🚶 Kilo ver',
  gain_weight_sport:'💪 Kilo al', gain_weight_no_sport:'🍽️ Kilo al',
  lose_weight:'Kilo ver', maintain:'Kilonu koru', gain_weight:'Kilo al',
};

/* ── Aylık plan kotası (localStorage tabanlı UI göstergesi) ──────── */
const PLAN_LIMIT_FREE = 3, PLAN_LIMIT_PREMIUM = 30;

// İlk plan tarihinden 1 ay geçtiyse sayacı sıfırlar; mevcut durumu döner.
function planQuotaRead() {
  try {
    let firstDate = localStorage.getItem('diyet_first_plan_date');
    let count = parseInt(localStorage.getItem('diyet_plan_count') || '0', 10);
    if (!Number.isFinite(count) || count < 0) count = 0;
    if (firstDate) {
      const renew = new Date(firstDate);
      if (!isNaN(renew.getTime())) {
        renew.setMonth(renew.getMonth() + 1);
        if (Date.now() >= renew.getTime()) {
          firstDate = null; count = 0;
          localStorage.removeItem('diyet_first_plan_date');
          localStorage.setItem('diyet_plan_count', '0');
        }
      }
    }
    return { firstDate, count };
  } catch { return { firstDate: null, count: 0 }; }
}

// Yeni plan üretildiğinde çağrılır: sayacı +1, ilk tarih yoksa bugünü yazar.
function planQuotaBump() {
  let { firstDate, count } = planQuotaRead();
  try {
    if (!firstDate) {
      firstDate = new Date().toISOString();
      localStorage.setItem('diyet_first_plan_date', firstDate);
    }
    count += 1;
    localStorage.setItem('diyet_plan_count', String(count));
  } catch { /* yoksay */ }
  return { firstDate, count };
}

// İlk tarih + 1 ay → okunur yenileme tarihi.
function planRenewalLabel(firstDate) {
  if (!firstDate) return '—';
  const d = new Date(firstDate);
  if (isNaN(d.getTime())) return '—';
  d.setMonth(d.getMonth() + 1);
  return d.toLocaleDateString('tr-TR', { day:'2-digit', month:'long', year:'numeric' });
}

const GOALS_SPORT = [
  { value:'cut',               icon:'🔥', label:'Cut',                   sub:'Yağ yak, kas koru', cal:'-350 kcal', color:'#ef4444' },
  { value:'bulk',              icon:'🏋️', label:'Bulk',                  sub:'Kas kazan',          cal:'+600 kcal', color:'#a855f7' },
  { value:'maintain_sport',    icon:'⚡', label:'Kilomi koruyacağım',    sub:'Maingaining',        cal:'0 kcal',    color:'#f59e0b' },
  { value:'aggressive_cut', icon:'🔥🔥', label:'Aggressive Cut', sub:'Hızlı yağ yakma', cal:'-800 kcal', color:'#ef4444' },
];
const GOALS_NO_SPORT = [
  { value:'lose_weight_no_sport', icon:'🚶', label:'Kilo vermek istiyorum', sub:'', cal:'-500 kcal', color:'#86efac' },
  { value:'gain_weight_no_sport', icon:'🥄', label:'Kilo almak istiyorum',  sub:'', cal:'+300 kcal', color:'#93c5fd' },
  { value:'maintain_no_sport',    icon:'⚖️', label:'Kilomi koruyacağım',    sub:'', cal:'0 kcal',    color:'#fcd34d' },
];

const HEALTH_FACTS = [
  { icon:'💧', text:'Günde 8 bardak su içmek metabolizmanı %30 hızlandırır' },
  { icon:'🥩', text:'Protein, tokluk hissini en uzun süre koruyan makrobesindir' },
  { icon:'🌙', text:"Gece 23:00'dan sonra yenen yiyecekler yağ olarak depolanma ihtimali daha yüksektir" },
  { icon:'🥦', text:'Yeşil sebzelerin lif içeriği sindirim sistemini düzenler ve bağırsak sağlığını korur' },
  { icon:'⚡', text:'Egzersizden 1-2 saat önce karbonhidrat tüketmek performansı artırır' },
  { icon:'🫀', text:'Omega-3 yağ asitleri kalp sağlığını korur — haftada 2 porsiyon balık yeterli' },
  { icon:'🧠', text:'Kahvaltı yapmak gün boyu odaklanmayı ve enerji seviyesini dengeler' },
  { icon:'🔥', text:'Kas kütlesi arttıkça bazal metabolizma hızı da artar — dinlenirken daha fazla kalori yakarsın' },
];

const ACTIVITIES = [
  { value:'sedentary',   label:'Hareketsiz',  sub:'Masa başı, az yürüyüş' },
  { value:'light',       label:'Az Aktif',    sub:'Haftada 1-3 gün egzersiz' },
  { value:'moderate',    label:'Orta Aktif',  sub:'Haftada 3-5 gün egzersiz' },
  { value:'active',      label:'Aktif',       sub:'Haftada 6-7 gün yoğun' },
  { value:'very_active', label:'Çok Aktif',   sub:'Günde 2 antrenman' },
];

const DAYS_TR = ['Pazartesi','Salı','Çarşamba','Perşembe','Cuma','Cumartesi','Pazar'];

const MACRO_CONFIG = [
  { key:'protein_g',  label:'Protein',       unit:'g', color:'#22c55e', max:300, icon:'🥩' },
  { key:'carbs_g',    label:'Karbonhidrat',  unit:'g', color:'#3b82f6', max:500, icon:'🌾' },
  { key:'fat_g',      label:'Yağ',           unit:'g', color:'#f59e0b', max:200, icon:'🫒' },
  { key:'fiber_g',    label:'Lif',           unit:'g', color:'#a855f7', max:50,  icon:'🥦', fixed:true },
  { key:'sugar_g',    label:'Şeker (maks)',  unit:'g', color:'#ec4899', max:100, icon:'🍭', fixed:true, isMax:true },
  { key:'sodium_g',   label:'Sodyum (maks)', unit:'g', color:'#6366f1', max:5,   icon:'🧂', fixed:true, isMax:true },
];

/* ─── HEDEF UYUMLULUK KONTROLÜ ──────────────────────────────── */
function getDisabledGoals(form) {
  const w   = Number(form.weight_kg)       || 0;
  const tw  = Number(form.target_weight_kg) || 0;
  const bf  = Number(form.body_fat_pct)    || 0;
  const male = form.gender === 'male';
  const dis  = {};

  // Mevcut ve hedef kilo karşılaştırması
  if (w > 0 && tw > 0) {
    const diff = tw - w;
    if (diff > 1) {
      // Hedef kilo > mevcut → kilo almak istiyor → sadece kilo alma aktif
      const msg = '⚠️ Kilonuzla uyumsuz — hedefiniz mevcut kilonuzdan yüksek';
      dis.cut                  = msg;
      dis.aggressive_cut       = msg;
      dis.lose_weight_no_sport = msg;
      dis.maintain_sport       = msg;
      dis.maintain_no_sport    = msg;
    } else if (diff < -1) {
      // Hedef kilo < mevcut → kilo vermek istiyor → sadece kilo verme aktif
      const msg = '⚠️ Kilonuzla uyumsuz — hedefiniz mevcut kilonuzdan düşük';
      dis.bulk                 = msg;
      dis.gain_weight_no_sport = msg;
      dis.maintain_sport       = msg;
      dis.maintain_no_sport    = msg;
    } else {
      // Hedef ≈ mevcut → sadece koruma hedefleri aktif
      const msg = '⚠️ Hedefinize zaten yakınsınız — koruma modunu seçin';
      dis.cut                  = msg;
      dis.bulk                 = msg;
      dis.aggressive_cut       = msg;
      dis.lose_weight_no_sport = msg;
      dis.gain_weight_no_sport = msg;
    }
  }

  // Vücut yağı kontrolü
  if (bf > 0) {
    const yuksekYag = male ? bf > 20 : bf > 28;
    const dusukYag  = male ? bf < 10 : bf < 18;
    if (yuksekYag && !dis.bulk)
      dis.bulk = '⚠️ Yağ oranınız yüksek — önce yağ yakmanızı öneririz';
    if (dusukYag && !dis.cut)
      dis.cut  = '⚠️ Yağ oranınız zaten düşük — cut uygun değil';
  }

  return dis;
}

/* ─── ANA BİLEŞEN ───────────────────────────────────────────── */
export default function App() {
  const [step, setStep]         = useState(1);
  const [dir,  setDir]          = useState('fwd');
  const [splash,    setSplash]    = useState(true);
  const [splashOut, setSplashOut] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState('');
  const [planGenerating, setPG] = useState(false);
  const [quotaModal, setQuotaModal] = useState(null); // aylık limit aşıldığında { plan, limit, message }
  const [dashboardMode, setDashboardMode] = useState(false); // plan onaylandıktan sonra mobil dashboard
  const [dashTab, setDashTab]   = useState('home'); // 'home' | 'plan' | 'water' | 'weight' | 'settings'
  const [planUsage, setPlanUsage] = useState(() => planQuotaRead()); // { firstDate, count }

  // Auth: { token, user } ya da null (guest)
  const [auth, setAuth]           = useState(null);
  const [authModal, setAuthModal] = useState(false); // giriş/kayıt modalı
  const [accountModal, setAccountModal] = useState(false); // hesap menüsü

  // Theme: 'dark' | 'light'
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('diyet_theme') || 'dark'; } catch { return 'dark'; }
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('diyet_theme', theme); } catch {}
  }, [theme]);

  // Tarayıcı ileri/geri tuşlarıyla /premium senkronu.
  useEffect(() => {
    const onPop = () => setShowPremium(
      typeof window !== 'undefined' && window.location.pathname === '/premium'
    );
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Toast notification
  const [toastMsg, setToastMsg] = useState('');
  const showToast = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(''), 2200); };

  // Su takibi
  const todayKey = getWaterKey(new Date());
  const [waterCount, setWaterCount] = useState(() => {
    try { return parseInt(localStorage.getItem(todayKey) || '0', 10); } catch { return 0; }
  });
  const [waterTarget, setWaterTarget] = useState(() => {
    try { return parseInt(localStorage.getItem('diyet_water_target') || '8', 10); } catch { return 8; }
  });

  // Favori / Geçmiş planlar
  const [savedPlans, setSavedPlans] = useState(() => {
    try { return JSON.parse(localStorage.getItem('diyet_saved_plans') || '[]'); } catch { return []; }
  });

  // Günlük yemek günlüğü (plan dışı yenenler) — takvim günü bazlı, localStorage
  const todayLogKey = `diyet_foodlog_${new Date().toISOString().split('T')[0]}`;
  const [foodLog, setFoodLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem(todayLogKey) || '[]'); } catch { return []; }
  });
  const [foodModal, setFoodModal] = useState(false);
  const [nudgeHidden, setNudgeHidden] = useState(false);
  // /premium rotası — header, Ayarlar sekmesi ve kota modalından erişilir.
  const [showPremium, setShowPremium] = useState(
    typeof window !== 'undefined' && window.location.pathname === '/premium'
  );
  const goPremium = () => {
    setShowPremium(true);
    try { window.history.pushState({ premium: true }, '', '/premium'); } catch {}
    try { window.scrollTo(0, 0); } catch {}
  };
  const closePremium = () => {
    setShowPremium(false);
    try { if (window.location.pathname === '/premium') window.history.pushState({}, '', '/'); } catch {}
  };
  // Ödeme entegrasyonu sonra bağlanacak — şimdilik placeholder.
  const onUpgradeClick = () => { console.log('iyzico ödeme akışı buraya'); };
  const [copyDayModal, setCopyDayModal] = useState(null);  // { from: dayIndex }
  const [recipeModal, setRecipeModal] = useState(null);    // { loading, data, mealName }
  // "Bir daha önerme" denen ürünler (plan üretiminde dışlanır).
  const [disliked, setDisliked] = useState(() => {
    try { return JSON.parse(localStorage.getItem('diyet_disliked') || '[]'); } catch { return []; }
  });

  // Besin detay modalı { item, di, mi, ii }
  const [nutritionModal, setNutritionModal] = useState(null);

  // Bildirim / Hatırlatıcı
  const [notifEnabled, setNotifEnabled] = useState(() => {
    try { return localStorage.getItem('diyet_notif') === '1'; } catch { return false; }
  });
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  // Fiyat tazeliği { latest, days_ago }
  const [priceFreshness, setPriceFreshness] = useState(null);

  const [calories, setCalories] = useState(null);
  const [planData, setPlanData] = useState(null);   // mutable plan (alternatifler eklenebilir)
  const [openDay,  setOpenDay]  = useState(0);      // bugün = 0

  // Yedim/yemedim haritası: `${di}-${mi}-${ii}` → true/false/undefined
  const [eatenMap, setEatenMap] = useState({});

  // Alternatif modal
  const [altModal,      setAltModal]      = useState(null); // { item, di, mi, ii }
  const [altStep,       setAltStep]       = useState('choose'); // 'choose'|'loading'|'result'|'error'
  const [altResult,     setAltResult]     = useState(null);
  const [altTargetMeal, setAltTargetMeal] = useState('');
  const [altTargetIdx,  setAltTargetIdx]  = useState(-1);

  // Ürün arama (adım 2)
  const [lockedProducts, setLocked]   = useState([]);
  const [searchQuery,    setSQ]        = useState('');
  const [searchResults,  setSearch]    = useState([]);
  const [searching,      setSearching] = useState(false);

  // PDF
  const [pdfDragging, setPdfDragging] = useState(false);
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [pdfResult,   setPdfResult]   = useState(null);
  const [calorieChoice, setCalChoice] = useState(null);
  const [targetCalOverride, setCalOverride] = useState(null);
  const pdfInputRef = useRef(null);

  const [form, setForm] = useState({
    age:'', height_cm:'', weight_kg:'', body_fat_pct:'', target_weight_kg:'',
    gender:'male', goal:'cut',
    activity_level:'moderate', meals_per_day:3,
    budget_tl:3000,
    selected_markets: MARKETS.filter(m => !m.yakinda).map(m => m.id),
    dietary_filters: [],
  });

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Açılış splash ekranı: göster → soldur → kaldır
  useEffect(() => {
    const t1 = setTimeout(() => setSplashOut(true), 1900);
    const t2 = setTimeout(() => setSplash(false),   2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Daha önce plan oluşturulmuşsa: state'leri geri yükle, dashboard'a (adım 5) git.
  useEffect(() => {
    try {
      const savedPlan = localStorage.getItem('diyet_plan');
      if (!savedPlan) return;
      setPlanData(JSON.parse(savedPlan));
      const savedCal = localStorage.getItem('diyet_calories');
      if (savedCal) setCalories(JSON.parse(savedCal));
      const savedForm = localStorage.getItem('diyet_form');
      if (savedForm) setForm(f => ({ ...f, ...JSON.parse(savedForm) }));
      setStep(0); setDashboardMode(true); setDashTab('home');
    } catch { /* bozuk veri → normal akış */ }
  }, []); // eslint-disable-line

  const goTo = (n) => { setDir(n > step ? 'fwd' : 'bck'); setStep(n); setError(''); };

  /* ── Auth: açılışta token'ı doğrula, profili forma doldur ──────── */
  useEffect(() => {
    const token = localStorage.getItem('diyet_token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { localStorage.removeItem('diyet_token'); return; }
        const { user } = await res.json();
        setAuth({ token, user });
        prefillFromUser(user);
      } catch { /* offline → guest devam */ }
    })();
  }, []); // eslint-disable-line

  // Giriş yapılınca bugünün yemek günlüğünü sunucudan çek (lokal boşsa doldur).
  useEffect(() => {
    if (!auth?.token) return;
    const today = new Date().toISOString().split('T')[0];
    fetch(`${API}/api/food-logs?date=${today}`, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        if (!Array.isArray(rows) || rows.length === 0) return;
        setFoodLog(prev => {
          if (prev.length > 0) return prev;   // lokal doluysa dokunma (çift kayıt önle)
          const mapped = rows.map((r, i) => ({
            ts: Date.now() + i, serverId: r.id, name: r.name, amount_g: r.amount_g,
            calories: r.calories, protein_g: r.protein_g, carbs_g: r.carbs_g, fat_g: r.fat_g,
          }));
          try { localStorage.setItem(todayLogKey, JSON.stringify(mapped)); } catch {}
          return mapped;
        });
      })
      .catch(() => {});
  }, [auth]); // eslint-disable-line

  // Kayıtlı profili forma yansıt (yalnızca dolu alanları).
  const prefillFromUser = (user) => {
    if (!user) return;
    setForm(f => ({
      ...f,
      age:            user.age            ?? f.age,
      height_cm:      user.height_cm      ?? f.height_cm,
      weight_kg:      user.weight_kg      ?? f.weight_kg,
      gender:         user.gender         ?? f.gender,
      goal:           user.goal           ?? f.goal,
      activity_level: user.activity_level ?? f.activity_level,
    }));
  };

  const handleAuthSuccess = ({ token, user }) => {
    localStorage.setItem('diyet_token', token);
    setAuth({ token, user });
    prefillFromUser(user);
    // Sunucuda profil yok ama kullanıcı zaten yerel olarak doldurmuşsa → sunucuya taşı.
    if (!user?.age && form.age && form.height_cm && form.weight_kg) syncProfile(form, token);
    setAuthModal(false);
  };

  const logout = () => {
    localStorage.removeItem('diyet_token');
    setAuth(null);
    setAccountModal(false);
  };

  const deleteAccount = async () => {
    if (!auth) return;
    if (!window.confirm('Hesabın ve tüm verilerin kalıcı olarak silinecek. Emin misin?')) return;
    try {
      await fetch(`${API}/api/auth/account`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${auth.token}` },
      });
    } catch { /* yine de lokal oturumu kapat */ }
    logout();
  };

  // Kilo / hedef kilo / yağ oranı değişince uyumsuz hedef seçiliyse sıfırla
  useEffect(() => {
    if (!form.weight_kg) return;
    const dis = getDisabledGoals(form);
    if (dis[form.goal]) {
      const allGoals = [...GOALS_SPORT, ...GOALS_NO_SPORT];
      const first = allGoals.find(g => !dis[g.value]);
      if (first) setForm(f => ({ ...f, goal: first.value }));
    }
  }, [form.weight_kg, form.target_weight_kg, form.body_fat_pct, form.gender]); // eslint-disable-line

  /* ── Kalori hesapla ─────────────────────────────────────────── */
  // Giriş yapan kullanıcının profilini sunucuya yaz (cihazlar arası senkron).
  // Ateşle-unut: başarısız olsa bile lokal akışı bloklamaz.
  const syncProfile = (profile, tokenOverride) => {
    const token = tokenOverride || auth?.token;
    if (!token) return;
    fetch(`${API}/api/auth/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        age: profile.age, height_cm: profile.height_cm, weight_kg: profile.weight_kg,
        gender: profile.gender, goal: profile.goal, activity_level: profile.activity_level,
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.user) setAuth(a => a ? { ...a, user: d.user } : a); })
      .catch(() => { /* offline → lokal veri yeterli */ });
  };

  // Ayarlar sekmesinden kilo/hedef kilo/aktivite güncelle: formu + lokal depoyu yaz,
  // kalori hedefini tazele ve giriş yapılıysa backend'e PATCH gönder.
  const saveProfileSettings = async ({ weight_kg, target_weight_kg, activity_level }) => {
    const w = parseFloat(weight_kg);
    if (!Number.isFinite(w) || w < 30 || w > 300) { showToast('⚠️ Geçerli bir kilo gir (30–300 kg)'); return; }
    const tw = parseFloat(target_weight_kg);
    const hasTw = Number.isFinite(tw) && tw >= 30 && tw <= 300;

    const next = {
      ...form,
      weight_kg: String(w),
      activity_level,
      target_weight_kg: hasTw ? String(tw) : '',
    };
    setForm(next);
    try {
      localStorage.setItem('diyet_form', JSON.stringify(next));
      if (hasTw) localStorage.setItem('diyet_target_weight', String(tw));
    } catch {}

    // Kalori hedefini yeni profile göre tazele (kilo/aktivite doğrudan etkiler).
    // Diyetisyen/adaptif override varsa onu koru.
    try {
      const res = await fetch(`${API}/api/calculate-calories`, {
        method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(next),
      });
      const cal = await res.json();
      if (res.ok && !cal.error) {
        const merged = targetCalOverride ? { ...cal, target_calories: targetCalOverride } : cal;
        setCalories(merged);
        try { localStorage.setItem('diyet_calories', JSON.stringify(merged)); } catch {}
      }
    } catch { /* offline → mevcut hedef kalsın */ }

    // Backend'e PATCH (giriş yapılıysa). target_weight_kg DB'de yok → kolonu olan alanları gönder.
    if (auth?.token) {
      try {
        const res = await fetch(`${API}/api/auth/profile`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
          body: JSON.stringify({ weight_kg: w, activity_level, goal: form.goal }),
        });
        if (res.ok) { const d = await res.json(); if (d?.user) setAuth(a => a ? { ...a, user: d.user } : a); }
      } catch { /* offline → lokal yeterli */ }
    }
    showToast('✅ Profil güncellendi');
  };

  const afterProfile = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/calculate-calories`, {
        method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.message || 'Bilgileri kontrol et (yaş 10-120, boy 100-250 cm, kilo 30-300 kg).');
        setLoading(false); return;
      }
      setCalories(data);
      syncProfile(form);   // profil tamamlandı → giriş yapılıysa sunucuya kaydet
      goTo(2);
    } catch { setError('Sunucuya bağlanılamadı.'); }
    setLoading(false);
  };

  /* ── Ürün arama ─────────────────────────────────────────────── */
  const searchTimer = useRef(null);
  const handleSearch = (q) => {
    setSQ(q); clearTimeout(searchTimer.current);
    if (q.length < 2) { setSearch([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try { const r = await fetch(`${API}/api/products/search?q=${encodeURIComponent(q)}`); setSearch(await r.json()); } catch {}
      setSearching(false);
    }, 350);
  };

  const addProduct = (p) => {
    if (lockedProducts.length >= 3 || lockedProducts.find(lp => lp.id === p.id)) return;
    const n = p.nutritions;
    setLocked(prev => [...prev, {
      id:p.id, name:p.name, amount_g:100,
      base_per_100g:{ calories:n?.calories||0, protein_g:n?.protein_g||0, carbs_g:n?.carbs_g||0, fat_g:n?.fat_g||0 },
      calories:n?.calories||0, protein_g:n?.protein_g||0, carbs_g:n?.carbs_g||0, fat_g:n?.fat_g||0,
    }]);
    setSQ(''); setSearch([]);
  };

  const updateAmount = (id, amt) => {
    const g = Math.max(1, Math.min(2000, Number(amt)||1));
    setLocked(prev => prev.map(lp => {
      if (lp.id !== id) return lp;
      const factor = g/100, base = lp.base_per_100g;
      return { ...lp, amount_g:g,
        calories:  Math.round(base.calories *factor),
        protein_g: Math.round(base.protein_g*factor*10)/10,
        carbs_g:   Math.round(base.carbs_g  *factor*10)/10,
        fat_g:     Math.round(base.fat_g    *factor*10)/10 };
    }));
  };

  const removeProduct = (id) => setLocked(p => p.filter(lp => lp.id !== id));

  /* ── PDF ─────────────────────────────────────────────────────── */
  const analyzePdf = async (file) => {
    if (!file || file.type !== 'application/pdf') { setError('PDF yükleyin.'); return; }
    setPdfLoading(true); setError(''); setPdfResult(null); setCalChoice(null);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      fd.append('selected_markets', JSON.stringify(form.selected_markets));
      fd.append('target_calories', String(calories?.target_calories||''));
      const res  = await fetch(`${API}/api/analyze-diet-pdf`, { method:'POST', body:fd });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setPdfResult(data);
    } catch (e) { setError(e.message||'PDF analiz edilemedi.'); }
    setPdfLoading(false);
  };

  const confirmPdfProducts = () => {
    const newLocked = [...lockedProducts];
    for (const fp of (pdfResult?.found_products||[])) {
      if (!newLocked.find(lp => lp.id===fp.id))
        newLocked.push({ id:fp.id, name:fp.name, amount_g:fp.amount_g, base_per_100g:fp.base_per_100g,
          calories:fp.calories, protein_g:fp.protein_g, carbs_g:fp.carbs_g, fat_g:fp.fat_g });
    }
    setLocked(newLocked);
    if (calorieChoice==='dietitian' && pdfResult?.calorie_warning) {
      const dc = pdfResult.calorie_warning.dietitian_calories;
      setCalOverride(dc); setCalories(c => ({ ...c, target_calories:dc }));
    }
    goTo(3);
  };

  /* ── Plan oluştur ───────────────────────────────────────────── */
  const generatePlan = async () => {
    setPG(true); setError(''); setOpenDay(0); setEatenMap({});
    try {
      const body = { ...form, locked_products:lockedProducts,
        ...(targetCalOverride ? { target_calories_override:targetCalOverride } : {}),
        dietary_filters: form.dietary_filters,
        excluded_products: disliked };
      const res  = await fetch(`${API}/api/meal-plan`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json',
          ...(auth ? { Authorization: `Bearer ${auth.token}` } : {}) },
        body:JSON.stringify(body),
      });
      const data = await res.json();
      if (res.status === 429 && data.error === 'limit_exceeded') {
        setPG(false); setQuotaModal(data); return;
      }
      if (data.error) throw new Error(data.message || data.error);
      setPlanData(JSON.parse(JSON.stringify(data))); // derin kopya (değiştirilebilir)
      // Tekrar açılışta dashboard'a dönebilmek için kaydet.
      try {
        localStorage.setItem('diyet_plan', JSON.stringify(data));
        if (calories) localStorage.setItem('diyet_calories', JSON.stringify(calories));
        localStorage.setItem('diyet_form', JSON.stringify(form));
      } catch { /* kota dolu → yoksay */ }
      // Geçmiş planlara ekle (son 5)
      try {
        const planEntry = {
          id: Date.now(),
          date: new Date().toLocaleDateString('tr-TR', { day:'2-digit', month:'long', year:'numeric' }),
          calories: data.weekly_avg_calories,
          cost: data.weekly_total_cost_tl,
          goal: form.goal,
        };
        const prev = JSON.parse(localStorage.getItem('diyet_saved_plans') || '[]');
        const updated = [planEntry, ...prev].slice(0, 5);
        localStorage.setItem('diyet_saved_plans', JSON.stringify(updated));
        setSavedPlans(updated);
      } catch {}
      setPlanUsage(planQuotaBump()); // aylık plan sayacını artır
      setPG(false); goTo(5);
    } catch (e) { setPG(false); setError(e.message||'Plan oluşturulamadı.'); }
  };

  /* ── Yedim/yemedim — iki ayrı buton için ───────────────────── */
  const setItemState = (di, mi, ii, target) => {
    const key = `${di}-${mi}-${ii}`;
    setEatenMap(prev => {
      if (prev[key] === target) {
        const { [key]:_, ...rest } = prev; return rest; // aynı buton → sıfırla
      }
      return { ...prev, [key]: target };
    });
  };

  /* ── Günlük yenilen kalori hesapla ─────────────────────────── */
  const getDayEaten = (di) => {
    if (!planData?.weekly_plan?.[di]) return { kcal:0, protein:0 };
    let kcal=0, protein=0;
    planData.weekly_plan[di].meals.forEach((meal, mi) => {
      (meal.items||[]).forEach((item, ii) => {
        if (eatenMap[`${di}-${mi}-${ii}`] === true) {
          kcal    += item.calories   || 0;
          protein += item.protein_g  || 0;
        }
      });
    });
    return { kcal:Math.round(kcal), protein:Math.round(protein*10)/10 };
  };

  /* ── Alternatif modal ───────────────────────────────────────── */
  const openAltModal = (di, mi, ii, item) => {
    setAltModal({ di, mi, ii, item });
    setAltStep('choose'); setAltResult(null); setAltTargetMeal(''); setAltTargetIdx(-1);
  };

  const selectAltMeal = async (mealName, mealIdx) => {
    if (!altModal) return;
    setAltTargetMeal(mealName); setAltTargetIdx(mealIdx); setAltStep('loading');
    const { di, item } = altModal;
    const { kcal:eatenKcal, protein:eatenPro } = getDayEaten(di);
    const targetKcal   = calories?.target_calories || 2000;
    const targetPro    = calories?.macros?.protein_g || 150;
    const remaining_kcal    = Math.max(0, targetKcal - eatenKcal + (item.calories||0));
    const remaining_protein = Math.max(0, targetPro  - eatenPro  + (item.protein_g||0));
    try {
      const res = await fetch(`${API}/api/alternative-meal`, {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({
          product_name:item.product_name, item_kcal:item.calories, item_protein_g:item.protein_g,
          target_meal:mealName, remaining_kcal, remaining_protein_g:remaining_protein,
          selected_markets:form.selected_markets,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setAltResult(data); setAltStep('result');
    } catch { setAltStep('error'); }
  };

  const confirmAlternative = (altItem) => {
    if (!altModal || !planData) return;
    const { di } = altModal;
    const mealIdx = altTargetIdx;
    if (mealIdx < 0 || !planData.weekly_plan[di].meals[mealIdx]) return;
    const newPlan = JSON.parse(JSON.stringify(planData));
    const meal = newPlan.weekly_plan[di].meals[mealIdx];
    meal.items = [...(meal.items||[]), { ...altItem, is_alternative:true }];
    meal.total_calories   = Math.round((meal.total_calories  ||0) + (altItem.calories  ||0));
    meal.total_protein_g  = Math.round(((meal.total_protein_g||0) + (altItem.protein_g ||0))*10)/10;
    meal.total_cost_tl    = Math.round(((meal.total_cost_tl  ||0) + (altItem.price_tl  ||0))*100)/100;
    setPlanData(newPlan); setAltModal(null);
  };

  /* ── Restart ────────────────────────────────────────────────── */
  const restart = () => {
    try {
      localStorage.removeItem('diyet_plan');
      localStorage.removeItem('diyet_calories');
      localStorage.removeItem('diyet_form');
    } catch { /* yoksay */ }
    setDashboardMode(false); setDashTab('home');
    setStep(1); setDir('fwd'); setPlanData(null); setCalories(null);
    setLocked([]); setError(''); setSQ(''); setSearch([]);
    setPdfResult(null); setCalChoice(null); setCalOverride(null);
    setEatenMap({}); setAltModal(null);
    setAltTargetMeal(''); setAltTargetIdx(-1);
  };

  // Plan özetinden dashboard'a geç.
  const confirmPlan = () => { setDashboardMode(true); setDashTab('home'); setStep(0); };

  /* ── Su takibi fonksiyonları ─────────────────────────────────── */
  const addWater = () => {
    if (waterCount >= waterTarget) return;
    const next = waterCount + 1;
    setWaterCount(next);
    try { localStorage.setItem(todayKey, String(next)); } catch {}
  };
  const removeWater = () => {
    if (waterCount <= 0) return;
    const next = waterCount - 1;
    setWaterCount(next);
    try { localStorage.setItem(todayKey, String(next)); } catch {}
  };
  const adjustWaterTarget = (delta) => {
    const next = Math.max(1, Math.min(20, waterTarget + delta));
    setWaterTarget(next);
    try { localStorage.setItem('diyet_water_target', String(next)); } catch {}
  };

  /* ── Yemek günlüğü fonksiyonları ─────────────────────────────── */
  const saveFoodLog = (list) => {
    setFoodLog(list);
    try { localStorage.setItem(todayLogKey, JSON.stringify(list)); } catch {}
  };
  const addFoodLog = (entry) => {
    const ts = Date.now();
    saveFoodLog([{ ...entry, ts }, ...foodLog]);
    setFoodModal(false);
    showToast(`✅ ${entry.name} günlüğe eklendi`);
    // Giriş yapılıysa sunucuya da yaz (cihazlar arası). serverId'yi geri damgala.
    if (auth?.token) {
      fetch(`${API}/api/food-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
        body: JSON.stringify({
          log_date: new Date().toISOString().split('T')[0],
          name: entry.name, amount_g: entry.amount_g,
          calories: entry.calories, protein_g: entry.protein_g,
          carbs_g: entry.carbs_g, fat_g: entry.fat_g,
        }),
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d?.id) return;
          setFoodLog(prev => {
            const next = prev.map(f => f.ts === ts ? { ...f, serverId: d.id } : f);
            try { localStorage.setItem(todayLogKey, JSON.stringify(next)); } catch {}
            return next;
          });
        })
        .catch(() => {});
    }
  };
  const removeFoodLog = (ts) => {
    const entry = foodLog.find(f => f.ts === ts);
    if (entry?.serverId && auth?.token) {
      fetch(`${API}/api/food-logs/${entry.serverId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${auth.token}` },
      }).catch(() => {});
    }
    saveFoodLog(foodLog.filter(f => f.ts !== ts));
  };
  const foodLogTotals = foodLog.reduce((a, f) => ({
    kcal: a.kcal + (f.calories || 0),
    protein: a.protein + (f.protein_g || 0),
    carbs: a.carbs + (f.carbs_g || 0),
    fat: a.fat + (f.fat_g || 0),
    fiber: a.fiber + (f.fiber_g || 0),
    sugar: a.sugar + (f.sugar_g || 0),
    sodium: a.sodium + (f.sodium_mg || 0),
  }), { kcal: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0 });

  /* ── Fiyat tazeliğini bir kez çek (plan varken) ──────────────── */
  useEffect(() => {
    if (!planData || priceFreshness) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/price-freshness`);
        if (r.ok) setPriceFreshness(await r.json());
      } catch { /* sessiz geç */ }
    })();
  }, [planData, priceFreshness]);

  /* ── Paylaşma fonksiyonu ────────────────────────────────────── */
  const shareDayPlan = (day, di) => {
    let text = `📅 ${DAYS_TR[di] || `${di+1}. Gün`} - Diyet Planı\n\n`;
    day.meals?.forEach(meal => {
      text += `🍽️ ${meal.meal_name}\n`;
      (meal.items||[]).forEach(item => {
        text += `  • ${item.product_name} (${item.amount_g}g) — ${item.calories} kcal\n`;
      });
      text += `  Toplam: ${meal.total_calories} kcal | ${meal.total_protein_g}g protein | ${meal.total_cost_tl} ₺\n\n`;
    });
    text += `🔥 Günlük: ${day.daily_calories} kcal | 💰 ${day.daily_cost_tl} ₺`;
    return text;
  };
  const copyDayPlan = (day, di) => {
    const text = shareDayPlan(day, di);
    navigator.clipboard?.writeText(text).then(() => showToast('📋 Plan panoya kopyalandı!'));
  };
  const whatsappDayPlan = (day, di) => {
    const text = shareDayPlan(day, di);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  // Tüm haftayı + alışveriş listesini metin olarak üret.
  const fullPlanText = () => {
    if (!planData?.weekly_plan?.length) return '';
    let t = `🥗 7 Günlük Diyet Planım\n`;
    t += `🔥 Ort. ${planData.weekly_avg_calories} kcal/gün | 💪 ${planData.weekly_avg_protein_g}g protein | 💰 Haftalık ${planData.weekly_total_cost_tl} ₺\n\n`;
    planData.weekly_plan.forEach((day, di) => { t += shareDayPlan(day, di) + '\n\n'; });
    if (planData.shopping_list?.length) {
      t += `🛒 Alışveriş Listesi\n`;
      planData.shopping_list.forEach(s => {
        t += `  • ${s.product}${s.quantity ? ` (${s.quantity})` : ''}${s.market ? ` — ${s.market}` : ''}${s.price_tl ? ` ${s.price_tl}₺` : ''}\n`;
      });
    }
    return t.trim();
  };
  // Mobilde yerel paylaşım sayfası, masaüstünde panoya kopyalama.
  const exportFullPlan = async () => {
    const text = fullPlanText();
    if (!text) return;
    if (navigator.share) {
      try { await navigator.share({ title: 'Diyet Planım', text }); return; } catch { /* iptal → panoya düş */ }
    }
    navigator.clipboard?.writeText(text).then(() => showToast('📋 Tüm plan panoya kopyalandı!'));
  };

  /* ── Bildirim / Hatırlatıcı ─────────────────────────────────── */
  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === 'granted') {
      const on = !notifEnabled;
      setNotifEnabled(on);
      try { localStorage.setItem('diyet_notif', on ? '1' : '0'); } catch {}
      if (on) showToast('🔔 Hatırlatıcılar aktif!');
      else     showToast('🔕 Hatırlatıcılar kapatıldı');
    } else {
      showToast('⚠️ Bildirim izni verilmedi — tarayıcı ayarlarından aktif edin');
    }
  };
  const toggleNotif = () => {
    if (notifPermission !== 'granted') { requestNotifPermission(); return; }
    const on = !notifEnabled;
    setNotifEnabled(on);
    try { localStorage.setItem('diyet_notif', on ? '1' : '0'); } catch {}
    showToast(on ? '🔔 Hatırlatıcılar aktif!' : '🔕 Hatırlatıcılar kapatıldı');
  };
  const sendNotif = (title, body) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try { new Notification(title, { body, icon: '/favicon.ico', badge: '/favicon.ico' }); } catch {}
  };

  // Öğün ve su hatırlatıcılarını kontrol et (her dakika)
  useEffect(() => {
    if (!notifEnabled || !planData) return;
    const REMINDERS = [
      { h:8,  m:0,  key:'breakfast', title:'🍳 Kahvaltı Zamanı!',     body:'Günün en önemli öğünü seni bekliyor.' },
      { h:10, m:0,  key:'water_am',  title:'💧 Su İçmeyi Unuttun mu?', body:'Sabah ortasında bir bardak su için.' },
      { h:13, m:0,  key:'lunch',     title:'🥗 Öğle Yemeği Vakti!',    body:'Bugünkü öğle planını incele.' },
      { h:16, m:0,  key:'water_pm',  title:'💧 Su Zamanı!',            body:'Öğleden sonra su içmeyi unutma.' },
      { h:19, m:0,  key:'dinner',    title:'🍽️ Akşam Yemeği!',         body:'Akşam öğünü planına göre hazırla.' },
    ];
    const check = () => {
      const now = new Date();
      const h = now.getHours(), m = now.getMinutes();
      const todayKey = now.toISOString().split('T')[0];
      REMINDERS.forEach(r => {
        if (r.h === h && r.m === m) {
          const lk = `diyet_notif_sent_${todayKey}_${r.key}`;
          if (!localStorage.getItem(lk)) {
            sendNotif(r.title, r.body);
            try { localStorage.setItem(lk, '1'); } catch {}
          }
        }
      });
    };
    check();
    const timer = setInterval(check, 60000);
    return () => clearInterval(timer);
  // eslint-disable-next-line
  }, [notifEnabled, planData]);

  /* ── Haftalık ilerleme hesapla ──────────────────────────────── */

  const getWeeklyProgress = () => {
    if (!planData?.weekly_plan) return { eaten: 0, skipped: 0, total: 0, pct: 0 };
    let eaten = 0, skipped = 0, total = 0;
    planData.weekly_plan.forEach((day, di) => {
      day.meals?.forEach((meal, mi) => {
        (meal.items||[]).forEach((_, ii) => {
          total++;
          const state = eatenMap[`${di}-${mi}-${ii}`];
          if (state === true) eaten++;
          else if (state === false) skipped++;
        });
      });
    });
    const tracked = eaten + skipped;
    const pct = tracked > 0 ? Math.round((eaten / tracked) * 100) : 0;
    return { eaten, skipped, total, pct };
  };

  /* ── Haftalık kalori chart verisi ───────────────────────────── */
  const getWeeklyCalorieData = () => {
    if (!planData?.weekly_plan) return [];
    return planData.weekly_plan.map((day, di) => {
      const eaten = getDayEaten(di);
      return {
        day: DAYS_TR[di]?.slice(0, 3) || `G${di+1}`,
        yenen: eaten.kcal,
        hedef: calories?.target_calories || 2000,
      };
    });
  };

  /* ── Streak (seri) hesapla ──────────────────────────────────── */
  const getStreak = () => {
    try {
      let streak = 0;
      const today = new Date();
      for (let i = 0; i < 30; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = `diyet_streak_${d.toISOString().split('T')[0]}`;
        if (localStorage.getItem(key) === '1') streak++;
        else if (i > 0) break;
      }
      return streak;
    } catch { return 0; }
  };

  /* ── Bugünkü streak'i kaydet (plan yüklendiyse) ─────────────── */
  useEffect(() => {
    if (!planData) return;
    const prog = getWeeklyProgress();
    if (prog.eaten > 0) {
      const key = `diyet_streak_${new Date().toISOString().split('T')[0]}`;
      try { localStorage.setItem(key, '1'); } catch {}
    }
  // eslint-disable-next-line
  }, [eatenMap, planData]);

  /* ── BMI Hesapla ─────────────────────────────────────────────── */
  const getBMI = () => {
    const w = Number(form.weight_kg), h = Number(form.height_cm);
    if (!w || !h) return null;
    const bmi = Math.round((w / ((h / 100) ** 2)) * 10) / 10;
    let cat, color;
    if (bmi < 18.5)     { cat = 'Zayıf';       color = '#3b82f6'; }
    else if (bmi < 25)  { cat = 'Normal';       color = '#22c55e'; }
    else if (bmi < 30)  { cat = 'Fazla Kilolu'; color = '#f59e0b'; }
    else                { cat = 'Obez';          color = '#ef4444'; }
    return { bmi, cat, color };
  };

  /* ── Kilo hedefine ulaşınca yeni plan ───────────────────────── */
  const regenerateForNewWeight = async (currentWeight) => {
    up('weight_kg', String(currentWeight));
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/calculate-calories`, {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ ...form, weight_kg: currentWeight }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.message || 'Kalori hesaplanamadı.'); setLoading(false); return; }
      setCalories(data);
      syncProfile({ ...form, weight_kg: currentWeight });   // yeni kiloyu sunucuya da yaz
      setDashboardMode(false); setDir('fwd'); setStep(4);
    } catch { setError('Sunucuya bağlanılamadı.'); }
    setLoading(false);
  };

  // Adaptif hedef önerisini uygula: kalori hedefini güncelle + bir sonraki plan bunu kullansın.
  const applyAdaptiveTarget = (newTarget) => {
    setCalOverride(newTarget);
    setCalories(c => {
      const next = c ? { ...c, target_calories: newTarget } : c;
      try { if (next) localStorage.setItem('diyet_calories', JSON.stringify(next)); } catch {}
      return next;
    });
    showToast(`🎯 Kalori hedefin ${newTarget} kcal olarak güncellendi`);
  };

  // #4 — Ürünü "bir daha önerme" listesine ekle.
  const dislikeProduct = (name) => {
    const n = (name || '').trim();
    if (!n) return;
    setDisliked(prev => {
      if (prev.some(x => x.toLowerCase() === n.toLowerCase())) return prev;
      const next = [...prev, n];
      try { localStorage.setItem('diyet_disliked', JSON.stringify(next)); } catch {}
      return next;
    });
    showToast(`🚫 "${n}" bir daha önerilmeyecek`);
  };
  const removeDisliked = (name) => {
    setDisliked(prev => {
      const next = prev.filter(x => x !== name);
      try { localStorage.setItem('diyet_disliked', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // #5 — Bir günün öğünlerini başka güne kopyala.
  const applyCopyDay = (toIndex) => {
    if (!copyDayModal || !planData?.weekly_plan) return;
    const from = copyDayModal.from;
    if (from === toIndex) { setCopyDayModal(null); return; }
    setPlanData(prev => {
      const wp = prev.weekly_plan.map(d => ({ ...d }));
      const src = wp[from];
      wp[toIndex] = {
        ...wp[toIndex],
        meals: JSON.parse(JSON.stringify(src.meals || [])),
        daily_calories: src.daily_calories,
        daily_protein_g: src.daily_protein_g,
        daily_cost_tl: src.daily_cost_tl,
      };
      const next = { ...prev, weekly_plan: wp };
      try { localStorage.setItem('diyet_plan', JSON.stringify(next)); } catch {}
      return next;
    });
    setCopyDayModal(null);
    showToast(`🗓️ ${DAYS_TR[copyDayModal.from]} → ${DAYS_TR[toIndex]} kopyalandı`);
  };

  // #6 — Öğün için hazırlanış tarifi getir.
  const openRecipe = async (meal) => {
    setRecipeModal({ loading: true, data: null, mealName: meal.meal_name });
    try {
      const res = await fetch(`${API}/api/recipe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meal_name: meal.meal_name, items: meal.items }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setRecipeModal({ loading: false, data, mealName: meal.meal_name });
    } catch {
      setRecipeModal({ loading: false, data: null, mealName: meal.meal_name, error: true });
    }
  };

  const lockedKcal = lockedProducts.reduce((s,lp) => s+lp.calories, 0);
  const lockedPct  = calories ? Math.min(100, Math.round((lockedKcal/calories.target_calories)*100)) : 0;
  const step1Valid = form.age && form.height_cm && form.weight_kg;

  /* ── Render ─────────────────────────────────────────────────── */
  if (planGenerating) return (
    <div className="app"><Header step={step} plan={planData} onWeightClick={() => {}}
      auth={auth} onAuthClick={() => auth ? setAccountModal(true) : setAuthModal(true)} /><PlanLoadingScreen /></div>
  );

  // Hem step akışında hem dashboard'da görünen ortak modallar.
  const modals = (
    <>
      {altModal && (
        <AlternativeModal
          altModal={altModal} altStep={altStep} altResult={altResult}
          altTargetMeal={altTargetMeal}
          meals={planData?.weekly_plan?.[altModal.di]?.meals || []}
          onSelectMeal={selectAltMeal}
          onConfirm={confirmAlternative}
          onClose={() => setAltModal(null)}
        />
      )}

      {nutritionModal && (
        <NutritionDetailModal
          item={nutritionModal}
          onClose={() => setNutritionModal(null)}
          onDislike={(name) => { dislikeProduct(name); setNutritionModal(null); }}
        />
      )}

      {copyDayModal && (
        <div className="alt-overlay" onClick={() => setCopyDayModal(null)}>
          <div className="quota-modal" onClick={e => e.stopPropagation()}>
            <button className="alt-modal-close quota-close" onClick={() => setCopyDayModal(null)}>✕</button>
            <div className="quota-icon">🗓️</div>
            <h3 className="quota-title">{DAYS_TR[copyDayModal.from]} gününü kopyala</h3>
            <p className="quota-text">Hangi güne yapıştıralım?</p>
            <div className="copyday-grid">
              {DAYS_TR.map((d, i) => (
                <button key={i} className="copyday-btn" disabled={i === copyDayModal.from}
                  onClick={() => applyCopyDay(i)}>{d}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {recipeModal && (
        <RecipeModal data={recipeModal} onClose={() => setRecipeModal(null)} />
      )}

      {quotaModal && (
        <div className="alt-overlay" onClick={() => setQuotaModal(null)}>
          <div className="quota-modal" onClick={e => e.stopPropagation()}>
            <button className="alt-modal-close quota-close" onClick={() => setQuotaModal(null)}>✕</button>
            <div className="quota-icon">⭐</div>
            <h3 className="quota-title">Aylık Limitine Ulaştın</h3>
            <p className="quota-text">
              Bu ay için plan limitinize ulaştınız. Premium'a geçerek devam edebilirsiniz.
            </p>
            <button className="btn-primary full" onClick={() => { setQuotaModal(null); goPremium(); }}>
              ⭐ Premium'u Keşfet
            </button>
            <button className="quota-later" onClick={() => setQuotaModal(null)}>Daha sonra</button>
          </div>
        </div>
      )}

      {foodModal && (
        <FoodLogModal onAdd={addFoodLog} onClose={() => setFoodModal(false)} />
      )}

      {authModal && (
        <AuthModal onSuccess={handleAuthSuccess} onClose={() => setAuthModal(false)} />
      )}

      {accountModal && auth && (
        <div className="alt-overlay" onClick={() => setAccountModal(false)}>
          <div className="quota-modal" onClick={e => e.stopPropagation()}>
            <button className="alt-modal-close quota-close" onClick={() => setAccountModal(false)}>✕</button>
            <div className="quota-icon">👤</div>
            <h3 className="quota-title">{auth.user?.name || 'Hesabım'}</h3>
            <p className="quota-text">{auth.user?.email}<br/>
              <span className={`plan-badge ${auth.user?.plan === 'premium' ? 'premium' : ''}`}>
                {auth.user?.plan === 'premium' ? '⭐ Premium' : 'Ücretsiz'} üyelik
              </span>
            </p>
            <button className="btn-secondary full" onClick={logout}>Çıkış Yap</button>
            <button className="quota-later acc-delete" onClick={deleteAccount}>Hesabımı Sil</button>
          </div>
        </div>
      )}
    </>
  );

  /* ── PREMIUM SAYFASI (/premium) ─────────────────────────────── */
  if (showPremium) {
    return <PremiumPage auth={auth} onUpgradeClick={onUpgradeClick} onBack={closePremium} />;
  }

  /* ── DASHBOARD MODU (plan onaylandıktan sonra) ──────────────── */
  if (dashboardMode && planData) {
    const homeCal = calories?.target_calories || planData.weekly_avg_calories;
    const homePro = calories?.macros?.protein_g || planData.weekly_avg_protein_g;
    return (
      <div className="app">
        <Header dashboard plan={planData} auth={auth}
          onPremiumClick={goPremium}
          onAuthClick={() => auth ? setAccountModal(true) : setAuthModal(true)} />
        {modals}

        <main className="dash-main">
          <AnimatePresence mode="wait">
            <motion.div
              key={dashTab}
              initial={{ opacity: 0, y: 10, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -10, filter: 'blur(8px)' }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {dashTab === 'home' && (
                <div className="dash-home">
              <h2 className="dash-title">
                Merhaba{auth?.user?.name ? `, ${auth.user.name.split(' ')[0]}` : ''} 👋
              </h2>
              {(() => {
                const nudgeKey = `diyet_nudge_${new Date().toISOString().split('T')[0]}`;
                let dismissed = nudgeHidden;
                try { dismissed = dismissed || !!localStorage.getItem(nudgeKey); } catch {}
                if (dismissed) return null;
                const wlog = readWeightLog();
                const lastWeightDays = wlog[0]?.dateISO
                  ? Math.floor((Date.now() - new Date(wlog[0].dateISO + 'T12:00:00').getTime()) / 86400000) : null;
                const nudge = getHomeNudge({
                  foodCount: foodLog.length, waterCount, waterTarget,
                  eatenKcal: foodLogTotals.kcal, targetKcal: homeCal, lastWeightDays,
                });
                if (!nudge) return null;
                return (
                  <div className="home-nudge">
                    <span className="home-nudge-text">{nudge.text}</span>
                    <button className="home-nudge-x" onClick={() => {
                      try { localStorage.setItem(nudgeKey, '1'); } catch {}
                      setNudgeHidden(true);
                    }}>✕</button>
                  </div>
                );
              })()}
              <div className="dash-cards">
                <div className="dcard">
                  <span className="dcard-ic">🔥</span>
                  <span className="dcard-val">{homeCal || '—'}</span>
                  <span className="dcard-lbl">Günlük Kalori Hedefi</span>
                </div>
                <div className="dcard">
                  <span className="dcard-ic">💪</span>
                  <span className="dcard-val">{homePro ? `${homePro}g` : '—'}</span>
                  <span className="dcard-lbl">Günlük Protein Hedefi</span>
                </div>
                <div className="dcard" onClick={() => setDashTab('water')} style={{ cursor:'pointer' }}>
                  <span className="dcard-ic">💧</span>
                  <span className="dcard-val">{waterCount}/{waterTarget}</span>
                  <span className="dcard-lbl">Bugünkü Su</span>
                </div>
                <div className="dcard streak-card">
                  <span className="dcard-ic">🔥</span>
                  <span className="dcard-val streak-val">{getStreak()}</span>
                  <span className="dcard-lbl">Gün Serisi</span>
                </div>
              </div>

              {/* Bugünün Özeti */}
              {(() => {
                const todayDow = (new Date().getDay() + 6) % 7; // Pzt=0
                const todayPlan = planData.weekly_plan?.[todayDow];
                const todayEaten = getDayEaten(todayDow);
                const targetKcal = homeCal || 2000;
                const pct = Math.min(100, Math.round((todayEaten.kcal / targetKcal) * 100));
                if (!todayPlan) return null;
                return (
                  <div className="today-card">
                    <div className="today-card-head">
                      <div className="today-card-title"><span>📅</span> Bugün — {DAYS_TR[todayDow]}</div>
                      <button className="today-card-link" onClick={() => { setOpenDay(todayDow); setDashTab('plan'); }}>
                        Planı Gör →
                      </button>
                    </div>
                    <div className="today-ring-row">
                      <div className="today-ring">
                        <svg viewBox="0 0 80 80" width="80" height="80">
                          <circle cx="40" cy="40" r="30" fill="none" stroke="var(--s3, #1c2235)" strokeWidth="10" />
                          <circle cx="40" cy="40" r="30" fill="none"
                            stroke={pct >= 100 ? '#22c55e' : '#3b82f6'} strokeWidth="10"
                            strokeDasharray={`${2*Math.PI*30}`}
                            strokeDashoffset={`${2*Math.PI*30*(1-pct/100)}`}
                            strokeLinecap="round" transform="rotate(-90 40 40)"
                            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.34,1.56,0.64,1)' }}
                          />
                          <text x="40" y="38" textAnchor="middle" dominantBaseline="central"
                            fontSize="15" fontWeight="800" fill="var(--text, #f1f5f9)">{pct}%</text>
                          <text x="40" y="53" textAnchor="middle" dominantBaseline="central"
                            fontSize="8" fill="var(--t3, #94a3b8)">kcal</text>
                        </svg>
                      </div>
                      <div className="today-meals">
                        {todayPlan.meals?.map((meal, mi) => {
                          const mealEaten = (meal.items||[]).every((_,ii) => eatenMap[`${todayDow}-${mi}-${ii}`] === true);
                          const mealSkipped = (meal.items||[]).every((_,ii) => eatenMap[`${todayDow}-${mi}-${ii}`] === false);
                          return (
                            <div key={mi} className={`today-meal-row ${mealEaten?'eaten':mealSkipped?'skipped':''}` }>
                              <span className="tmr-dot">{mealEaten ? '✅' : mealSkipped ? '❌' : '⬜'}</span>
                              <span className="tmr-name">{meal.meal_name}</span>
                              <span className="tmr-kcal">{meal.total_calories} kcal</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="today-stats">
                      <span>Yenilen: <strong>{todayEaten.kcal} kcal</strong></span>
                      <span>Protein: <strong>{todayEaten.protein}g</strong></span>
                      <span>Kalan: <strong>{Math.max(0, targetKcal - todayEaten.kcal)} kcal</strong></span>
                    </div>
                  </div>
                );
              })()}

              {/* Günlük yemek günlüğü (barkod + arama + manuel) */}
              <FoodLogCard
                log={foodLog}
                totals={foodLogTotals}
                targetKcal={homeCal}
                targetPro={homePro}
                targetMacros={calories?.macros}
                onOpen={() => setFoodModal(true)}
                onRemove={removeFoodLog}
              />

              {/* Adaptif hedef / kilo trendi içgörüsü */}
              <TrendInsightCard
                trend={analyzeWeightTrend(readWeightLog(), form.goal, calories?.target_calories || homeCal)}
                onApply={applyAdaptiveTarget}
              />

              {/* Haftalık ilerleme */}
              <WeeklyProgressCard progress={getWeeklyProgress()} />

              {/* Kalori Grafiği */}
              {(() => {
                const chartData = getWeeklyCalorieData();
                const hasData = chartData.some(d => d.yenen > 0);
                if (!hasData) return null;
                return (
                  <div className="cal-chart-card">
                    <div className="cal-chart-title">📊 Haftalık Kalori Takibi</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={chartData} margin={{ top:5, right:10, left:-20, bottom:5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e2d47" />
                        <XAxis dataKey="day" stroke="#475569" tick={{ fontSize:10, fill:'#94a3b8' }} />
                        <YAxis stroke="#475569" tick={{ fontSize:10, fill:'#94a3b8' }} />
                        <Tooltip
                          contentStyle={{ background:'#0f1421', border:'1px solid #1e2d47', borderRadius:8, fontSize:12 }}
                          labelStyle={{ color:'#94a3b8' }}
                        />
                        <ReferenceLine y={calories?.target_calories||2000} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={1.5}
                          label={{ value:'Hedef', fill:'#ef4444', fontSize:9, position:'insideTopRight' }} />
                        <Line type="monotone" dataKey="yenen" name="Yenen" stroke="#22c55e" strokeWidth={2.5}
                          dot={{ fill:'#22c55e', r:4 }} activeDot={{ r:6, fill:'#4ade80' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              <div className="dash-motiv">
                <span className="dash-motiv-ic">✨</span>
                <p>Bugün hedefine bir adım daha yakınsın. Planına sadık kal, sonuçlar gelecek!</p>
              </div>
            </div>
          )}

          {dashTab === 'plan' && (
            <>
              <div className="plan-hero">
                <PlanStat val={planData.weekly_total_cost_tl+' ₺'} lbl="Haftalık Maliyet" />
                <div className="ph-div" />
                <PlanStat val={planData.weekly_avg_calories} lbl="Ort. Kalori / gün" unit="kcal" />
                <div className="ph-div" />
                <PlanStat val={planData.weekly_avg_protein_g+'g'} lbl="Ort. Protein / gün" />
              </div>
              {planData.budget_exceeded && (
                <div className="plan-warn">
                  ⚠️ Bu planın alışveriş sepeti ({planData.shopping_total_tl || planData.weekly_total_cost_tl} ₺) belirlediğin bütçeyi aşıyor.
                </div>
              )}
              <button className="btn-secondary full plan-export-btn" onClick={exportFullPlan}>
                📤 Tüm Planı Paylaş / Kopyala
              </button>
              <h3 className="section-lbl">📅 Günlük Planlar</h3>
              {planData.weekly_plan?.map((day, di) => {
                const eaten = getDayEaten(di);
                return (
                  <DayCard key={day.day} day={day} di={di}
                    open={openDay===di}
                    onToggle={() => setOpenDay(openDay===di ? -1 : di)}
                    eatenMap={eatenMap}
                    eatenKcal={eaten.kcal}
                    targetKcal={calories?.target_calories||2000}
                    onSetEaten={setItemState}
                    onAltClick={openAltModal}
                    onItemClick={(item) => setNutritionModal(item)}
                    onCopy={copyDayPlan}
                    onWhatsapp={whatsappDayPlan}
                    onCopyDay={(d) => setCopyDayModal({ from: d })}
                    onRecipe={openRecipe}
                  />
                );
              })}
              <h3 className="section-lbl" style={{ marginTop:28 }}>🛒 Alışveriş Listesi</h3>
              <ShoppingList items={planData.shopping_list} />
              <PriceFreshnessBadge data={priceFreshness} />
            </>
          )}

          {dashTab === 'water' && (
            <WaterTracker
              count={waterCount}
              target={waterTarget}
              onAdd={addWater}
              onRemove={removeWater}
              onAdjustTarget={adjustWaterTarget}
              weekData={getWeekWater()}
            />
          )}

          {dashTab === 'weight' && (
            <WeightTrackingTab
              startWeight={Number(form.weight_kg)||null}
              targetWeight={Number(form.target_weight_kg)||null}
              onRegenerate={regenerateForNewWeight}
              onWeightUpdate={kg => up('weight_kg', String(kg))}
              auth={auth}
            />
          )}

          {dashTab === 'settings' && (
            <div className="dash-settings">
              <h2 className="dash-title">Ayarlar</h2>

              {/* Tema geçişi */}
              <div className="theme-toggle-row">
                <div className="theme-toggle-label">
                  <span>{theme === 'dark' ? '🌙' : '☀️'}</span>
                  <span>{theme === 'dark' ? 'Karanlık Tema' : 'Aydınlık Tema'}</span>
                </div>
                <button className={`theme-switch ${theme === 'light' ? 'on' : ''}`}
                  onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} />
              </div>

              {/* Bildirim / Hatırlatıcı */}
              <div className="theme-toggle-row">
                <div className="theme-toggle-label">
                  <span>{notifEnabled ? '🔔' : '🔕'}</span>
                  <div>
                    <div>Öğün Hatırlatıcıları</div>
                    <div style={{ fontSize:11, color:'var(--t3)', fontWeight:400, marginTop:2 }}>
                      {notifPermission === 'denied'
                        ? '⚠️ İzin verilmedi — tarayıcı ayarlarından aktif edin'
                        : notifEnabled
                          ? 'Sabah, öğle, akşam + su hatırlatıcıları açık'
                          : 'Öğün ve su zamanı bildirimleri'}
                    </div>
                  </div>
                </div>
                <button
                  className={`theme-switch ${notifEnabled && notifPermission === 'granted' ? 'on' : ''}`}
                  onClick={toggleNotif}
                  disabled={notifPermission === 'denied'} />
              </div>

              {auth?.user?.plan !== 'premium' && (
                <button className="set-premium-cta" onClick={goPremium}>
                  <span className="set-premium-ic">⭐</span>
                  <span className="set-premium-txt">
                    <strong>Markilo Premium</strong>
                    <small>Sınırsız plan ve daha fazlası</small>
                  </span>
                  <span className="set-premium-arrow">›</span>
                </button>
              )}

              <ProfileEditCard form={form} auth={auth} bmi={getBMI()} streak={getStreak()} onSave={saveProfileSettings} />

              {/* Önerilmeyen ürünler (#4) */}
              {disliked.length > 0 && (
                <div className="set-card">
                  <h3 className="set-card-title">🚫 Önerilmeyen Ürünler</h3>
                  <div className="disliked-list">
                    {disliked.map((d, i) => (
                      <span key={i} className="disliked-chip">
                        {d}
                        <button onClick={() => removeDisliked(d)} title="Kaldır">✕</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Başarı rozetleri */}
              <BadgesSection badges={computeBadges({
                plansCount: savedPlans.length,
                streak: getStreak(),
                foodCount: getFoodLogCount(),
                weightCount: readWeightLog().length,
                waterMet: waterCount >= waterTarget,
              })} />

              {/* Geçmiş Planlar */}
              <SavedPlansSection plans={savedPlans} onLoad={(plan) => {
                try {
                  const saved = localStorage.getItem('diyet_plan');
                  if (saved) { setPlanData(JSON.parse(saved)); setDashTab('plan'); }
                } catch {}
              }} />

              <button className="btn-primary full" onClick={restart}>🔄 Yeni Plan Oluştur</button>
              {auth && (
                <button className="btn-secondary full" style={{ marginTop:10 }} onClick={logout}>Çıkış Yap</button>
              )}
            </div>
          )}
            </motion.div>
          </AnimatePresence>
        </main>

        {toastMsg && <div className="toast">{toastMsg}</div>}

        <nav className="bottom-tabbar">
          {[['home','🏠','Ana Sayfa'],['plan','📅','Plan'],['water','💧','Su'],['weight','⚖️','Kilo'],['settings','⚙️','Ayarlar']].map(([k,ic,lb]) => (
            <button key={k} className={`btab ${dashTab===k?'active':''}`} onClick={() => setDashTab(k)}>
              <span className="btab-ic">{ic}</span>
              <span className="btab-lb">{lb}</span>
            </button>
          ))}
        </nav>
      </div>
    );
  }

  return (
    <>
      {splash && (
        <div className={`splash ${splashOut ? 'out' : ''}`}>
          <div className="splash-inner">
            <div className="splash-title">Markilo</div>
            <div className="splash-symbol">🥗</div>
            <div className="splash-bar"><span /></div>
          </div>
        </div>
      )}
    <div className="app">
      <Header step={step} plan={planData}
        onWeightClick={() => { if (planData) { setDashboardMode(true); setDashTab('weight'); setStep(0); } }}
        onPremiumClick={goPremium}
        auth={auth} onAuthClick={() => auth ? setAccountModal(true) : setAuthModal(true)} />

      {modals}

      <main className="main" data-dir={dir}>
        <AnimatePresence mode="wait">
        {/* ── ADIM 1: PROFİL ──────────────────────────────── */}
        {step === 1 && (
          <Screen keyStr="step-1">
            <h2 className="s-title">Profilini Oluştur</h2>

            <FieldLabel>Cinsiyet</FieldLabel>
            <div className="gender-row">
              {[['male','👨 Erkek'],['female','👩 Kadın']].map(([v,lbl]) => (
                <button key={v} className={`gender-btn ${form.gender===v?'on':''}`}
                  onClick={() => up('gender',v)}>{lbl}</button>
              ))}
            </div>

            <div className="num-row">
              <NumInput label="Yaş"  unit="yıl" val={form.age}       onChange={v=>up('age',v)}       ph="25" />
              <NumInput label="Boy"  unit="cm"  val={form.height_cm} onChange={v=>up('height_cm',v)} ph="175" />
              <NumInput label="Kilo" unit="kg"  val={form.weight_kg} onChange={v=>up('weight_kg',v)} ph="70" />
            </div>

            <div className="num-row" style={{ marginTop:10 }}>
              <NumInput label="Vücut Yağ %" unit="%" val={form.body_fat_pct}
                onChange={v=>up('body_fat_pct',v)} ph="20" />
              <NumInput label="Hedef Kilo" unit="kg" val={form.target_weight_kg}
                onChange={v=>up('target_weight_kg',v)} ph="65" />
              <div className="ni-hint">ℹ️ İkisi de isteğe bağlı</div>
            </div>

            <FieldLabel>Hedefin ne?</FieldLabel>
            {(() => {
              const disabledGoals = getDisabledGoals(form);
              const GoalBtn = ({ g }) => {
                const dis = disabledGoals[g.value];
                const on  = form.goal === g.value;
                return (
                  <button key={g.value}
                    className={`goal-card ${on?'on':''} ${dis?'goal-disabled':''}`}
                    style={on && !dis ? { borderColor:g.color } : {}}
                    disabled={!!dis}
                    title={dis || ''}
                    onClick={() => up('goal', g.value)}>
                    <span className="g-icon">{g.icon}</span>
                    <span className="g-label">{g.label}</span>
                    {g.sub && <span className="g-sub">{g.sub}</span>}
                    <span className="g-cal" style={{ color: dis ? 'var(--t3)' : g.color }}>{g.cal}</span>
                    {dis && <span className="g-dis-tip">{dis}</span>}
                  </button>
                );
              };
              return (
                <div className="goal-columns">
                  <div className="goal-col">
                    <div className="goal-col-hdr sport">🏃 Spor Yapıyorum</div>
                    {GOALS_SPORT.map(g => <GoalBtn key={g.value} g={g} />)}
                  </div>
                  <div className="goal-col">
                    <div className="goal-col-hdr nosport">🛋️ Spor Yapmıyorum</div>
                    {GOALS_NO_SPORT.map(g => <GoalBtn key={g.value} g={g} />)}
                  </div>
                </div>
              );
            })()}

            <FieldLabel>Diyet Tercihleri</FieldLabel>
            <div className="diet-filter-grid">
              {DIETARY_FILTERS.map(f => {
                const on = form.dietary_filters.includes(f.id);
                return (
                  <button key={f.id} className={`diet-chip ${on?'on':''}`}
                    onClick={() => up('dietary_filters', on
                      ? form.dietary_filters.filter(x => x !== f.id)
                      : [...form.dietary_filters, f.id])}>
                    <span className="chip-icon">{f.icon}</span>
                    {f.label}
                    <span className="chip-check">{on ? '✓' : ''}</span>
                  </button>
                );
              })}
            </div>

            <FieldLabel>Aktivite Seviyesi</FieldLabel>
            <div className="act-list">
              {ACTIVITIES.map(a => (
                <button key={a.value} className={`act-row ${form.activity_level===a.value?'on':''}`}
                  onClick={() => up('activity_level',a.value)}>
                  <span className={`rdot ${form.activity_level===a.value?'on':''}`} />
                  <div><strong>{a.label}</strong><small>{a.sub}</small></div>
                </button>
              ))}
            </div>

            <FieldLabel>Günlük Öğün Sayısı</FieldLabel>
            <div className="meal-cnt">
              {[2,3,4,5,6].map(n => (
                <button key={n} className={`cnt-btn ${form.meals_per_day===n?'on':''}`}
                  onClick={() => up('meals_per_day',n)}>{n}</button>
              ))}
            </div>

            {error && <Err msg={error} />}
            <button className="btn-primary full" onClick={afterProfile} disabled={!step1Valid||loading}>
              {loading ? <Spin /> : 'Devam Et →'}
            </button>
          </Screen>
        )}

        {/* ── ADIM 2: ÜRÜN SEÇİMİ ─────────────────────────── */}
        {step === 2 && (
          <Screen keyStr="step-2">
            <h2 className="s-title">Ürün Seçimi</h2>
            <div className="pdf-section">
              <div className="pdf-section-title">
                <span>📋</span>
                <div><strong>Diyetisyen Programın Var mı?</strong><small>PDF yükle, ona göre optimize edelim</small></div>
              </div>
              {!pdfLoading && !pdfResult && (
                <PdfDropZone dragging={pdfDragging}
                  onDragOver={e => { e.preventDefault(); setPdfDragging(true); }}
                  onDragLeave={() => setPdfDragging(false)}
                  onDrop={e => { e.preventDefault(); setPdfDragging(false); const f=e.dataTransfer.files[0]; if(f) analyzePdf(f); }}
                  onClick={() => pdfInputRef.current?.click()} />
              )}
              <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" style={{ display:'none' }}
                onChange={e => { const f=e.target.files[0]; if(f) analyzePdf(f); e.target.value=''; }} />
              {pdfLoading && (
                <div className="pdf-analyzing">
                  <div className="pdf-analyzing-icon">🩺</div>
                  <div className="pdf-analyzing-text">Diyetisyen programınız analiz ediliyor...</div>
                  <DotsLoader />
                  <div className="pdf-analyzing-sub">Bu işlem 15–30 saniye sürebilir</div>
                </div>
              )}
              {pdfResult && !pdfLoading && (
                <PdfResults result={pdfResult} calorieChoice={calorieChoice}
                  onCalorieChoice={setCalChoice}
                  onReset={() => { setPdfResult(null); setCalChoice(null); }}
                  onConfirm={confirmPdfProducts} />
              )}
            </div>
            {!pdfResult && (
              <>
                <div className="pdf-or-divider">veya manuel ürün seç</div>
                <p className="s-sub">Mutlaka yemek istediğin ürünler var mı? En fazla 3 ekle.</p>
                <div className="search-wrap">
                  <span className="search-icon">🔍</span>
                  <input className="search-inp" type="text"
                    placeholder="Ürün adı ara... (ör: yumurta, tavuk göğsü)"
                    value={searchQuery} onChange={e => handleSearch(e.target.value)} />
                  {searching && <Spin />}
                </div>
                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map(p => (
                      <button key={p.id} className="search-result-item"
                        onClick={() => addProduct(p)}
                        disabled={lockedProducts.length>=3||!!lockedProducts.find(lp=>lp.id===p.id)}>
                        {p.health && <HealthScore data={p.health} />}
                        <div className="sri-info"><strong>{p.name}</strong><small>{p.nutritions?.calories} kcal / 100g · {p.nutritions?.protein_g}g protein</small></div>
                        <span className="add-ico">+</span>
                      </button>
                    ))}
                  </div>
                )}
                {lockedProducts.length > 0 && (
                  <div className="locked-list">
                    {lockedProducts.map((lp,i) => (
                      <LockedCard key={lp.id} lp={lp} dailyKcal={calories?.target_calories}
                        onChangeAmt={v=>updateAmount(lp.id,v)} onRemove={()=>removeProduct(lp.id)} animDelay={i*80} />
                    ))}
                    {calories && lockedProducts.length>0 && (
                      <div className="locked-summary">
                        <span>Seçilen toplam:</span>
                        <span className="ls-kcal">{lockedKcal} kcal</span>
                        <span className="ls-pct">= Günlük hedefin <strong>%{lockedPct}'i</strong></span>
                      </div>
                    )}
                  </div>
                )}
                <div className="btn-row">
                  <button className="btn-ghost" onClick={() => goTo(1)}>← Geri</button>
                  <button className="btn-primary" onClick={() => goTo(3)}>
                    {lockedProducts.length>0 ? `${lockedProducts.length} ürünle devam →` : 'Atla →'}
                  </button>
                </div>
              </>
            )}
            {error && <Err msg={error} />}
          </Screen>
        )}

        {/* ── ADIM 3: MARKET & BÜTÇE ──────────────────────── */}
        {step === 3 && (
          <Screen keyStr="step-3">
            <h2 className="s-title">Market & Bütçe</h2>
            <FieldLabel>Alışveriş yaptığın marketler</FieldLabel>
            <div className="market-grid">
              {MARKETS.map(m => {
                if (m.yakinda) {
                  return (
                    <div key={m.id} className="mkt-chip soon" aria-disabled="true" title="Çok yakında">
                      {m.id}<span className="mkt-soon-tag">Yakında</span>
                    </div>
                  );
                }
                const sel = form.selected_markets.includes(m.id);
                return (
                  <button key={m.id} className={`mkt-chip ${sel?'on':''}`}
                    style={sel?{ borderColor:m.color, color:m.color }:{}}
                    onClick={() => up('selected_markets', sel
                      ? form.selected_markets.filter(x=>x!==m.id)
                      : [...form.selected_markets, m.id])}>
                    {m.id}{sel && <span className="mkt-chk">✓</span>}
                  </button>
                );
              })}
            </div>
            <FieldLabel>Haftalık Bütçe</FieldLabel>
            <div className="budget-display">
              <span className="b-num">{form.budget_tl.toLocaleString('tr-TR')}</span>
              <span className="b-cur">₺</span>
            </div>
            <input type="range" className="slider" min={500} max={15000} step={100}
              value={form.budget_tl} onChange={e => up('budget_tl', Number(e.target.value))} />
            <div className="slider-ends"><span>500 ₺</span><span>15.000 ₺</span></div>
            <div className="btn-row">
              <button className="btn-ghost" onClick={() => goTo(2)}>← Geri</button>
              <button className="btn-primary" onClick={() => goTo(4)} disabled={form.selected_markets.length===0}>
                Kalori Özetini Gör →
              </button>
            </div>
          </Screen>
        )}

        {/* ── ADIM 4: KALORİ & MAKRO ÖZETİ ───────────────── */}
        {step === 4 && calories && (
          <Screen keyStr="step-4">
            <h2 className="s-title">Günlük Hedefler</h2>
            {targetCalOverride && (
              <div className="dietitian-badge">🩺 Diyetisyen hedefi: <strong>{targetCalOverride} kcal</strong></div>
            )}
            {form.body_fat_pct && (
              <div className="bodyfat-badge">
                💪 Vücut yağı <strong>%{form.body_fat_pct}</strong>
                {form.weight_kg && <span> · Yağsız kütle <strong>{Math.round(Number(form.weight_kg)*(1-Number(form.body_fat_pct)/100))} kg</strong></span>}
              </div>
            )}
            <div className="cal-hero">
              <div className="ch-side"><span className="ch-lbl">BMR</span><AnimNum target={calories.bmr} /><span className="ch-unit">kcal</span></div>
              <div className="ch-center"><span className="ch-lbl">Hedef</span><AnimNum target={calories.target_calories} big /><span className="ch-unit">kcal / gün</span></div>
              <div className="ch-side"><span className="ch-lbl">TDEE</span><AnimNum target={calories.tdee} /><span className="ch-unit">kcal</span></div>
            </div>
            <div className="macro-list">
              {MACRO_CONFIG.map((mc,i) => {
                // Tüm makrolar backend'den gelir (lif/şeker/sodyum artık kaloriye göre ölçeklenir).
                const val = calories.macros?.[mc.key] ?? 0;
                return <MacroRow key={mc.key} mc={mc} val={val} pct={Math.min(100,Math.round((val/mc.max)*100))} delay={i*100} isMax={mc.isMax} />;
              })}
            </div>
            {lockedProducts.length > 0 && (
              <div className="locked-summary" style={{ marginBottom:20 }}>
                <span>🔒 {lockedProducts.length} kilitli ürün:</span>
                <span className="ls-kcal">{lockedKcal} kcal</span>
                <span className="ls-pct">(%{lockedPct})</span>
              </div>
            )}
            <div className="btn-row">
              <button className="btn-ghost" onClick={() => goTo(3)}>← Geri</button>
              <button className="btn-primary" onClick={generatePlan}>🥗 7 Günlük Plan Oluştur</button>
            </div>
            {error && <Err msg={error} />}
          </Screen>
        )}

        {/* ── ADIM 5: PLAN + KİLO TAKİP ───────────────────── */}
        {/* ── ADIM 5: PLAN ÖZETİ (onay ekranı) ────────────── */}
        {step === 5 && planData && (
          <Screen keyStr="step-5">
            {(() => {
              const premium = auth?.user?.plan === 'premium';
              const limit = premium ? PLAN_LIMIT_PREMIUM : PLAN_LIMIT_FREE;
              const full  = planUsage.count >= limit;
              return (
                <div className={`quota-banner ${full ? 'full' : ''}`}>
                  {premium
                    ? `⭐ Bu ay ${planUsage.count}/${limit} plan hakkınızı kullandınız.`
                    : `Bu ay ${planUsage.count}/${limit} plan hakkınızı kullandınız. Yenileme: ${planRenewalLabel(planUsage.firstDate)}`}
                </div>
              );
            })()}

            <div className="plan-hero">
              <PlanStat val={planData.weekly_total_cost_tl+' ₺'} lbl="Haftalık Maliyet" />
              <div className="ph-div" />
              <PlanStat val={planData.weekly_avg_calories} lbl="Ort. Kalori / gün" unit="kcal" />
              <div className="ph-div" />
              <PlanStat val={planData.weekly_avg_protein_g+'g'} lbl="Ort. Protein / gün" />
            </div>

            <h3 className="section-lbl">📅 Günlük Planlar</h3>
            {planData.weekly_plan?.map((day, di) => {
              const eaten = getDayEaten(di);
              return (
                <DayCard key={day.day} day={day} di={di}
                  open={openDay===di}
                  onToggle={() => setOpenDay(openDay===di ? -1 : di)}
                  eatenMap={eatenMap}
                  eatenKcal={eaten.kcal}
                  targetKcal={calories?.target_calories||2000}
                  onSetEaten={setItemState}
                  onAltClick={openAltModal}
                  onItemClick={(item) => setNutritionModal(item)}
                  onCopy={copyDayPlan}
                  onWhatsapp={whatsappDayPlan}
                  onCopyDay={(d) => setCopyDayModal({ from: d })}
                  onRecipe={openRecipe}
                />
              );
            })}

            <h3 className="section-lbl" style={{ marginTop:28 }}>🛒 Alışveriş Listesi</h3>
            <ShoppingList items={planData.shopping_list} />
            <PriceFreshnessBadge data={priceFreshness} />

            {(() => {
              const premium = auth?.user?.plan === 'premium';
              const limit = premium ? PLAN_LIMIT_PREMIUM : PLAN_LIMIT_FREE;
              const full  = planUsage.count >= limit;
              return (
                <>
                  <div className="plan-actions">
                    <button className="btn-ghost" onClick={generatePlan} disabled={planGenerating || full}>
                      🔄 Başka Plan Oluştur
                    </button>
                    <button className="btn-primary" onClick={confirmPlan}>
                      ✓ Planı Onayla
                    </button>
                  </div>
                  {full && <p className="plan-limit-note">Aylık limitinize ulaştınız</p>}
                </>
              );
            })()}
          </Screen>
        )}

        </AnimatePresence>
      </main>
    </div>
    </>
  );
}

/* ══════════════ ALT BİLEŞENLER ════════════════════════════════ */

function AuthModal({ onSuccess, onClose }) {
  const [mode, setMode]   = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [pass,  setPass]  = useState('');
  const [name,  setName]  = useState('');
  const [kvkk,  setKvkk]  = useState(false); // KVKK aydınlatma metni onayı (yalnızca kayıt)
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState('');
  const [notice, setNotice] = useState(''); // bilgi mesajı (örn. şifre sıfırlama yakında)
  const googleBtnRef = useRef(null);

  const switchMode = (m) => { setMode(m); setErr(''); setNotice(''); };

  const submit = async () => {
    // Kayıtta KVKK onayı zorunlu — Enter ile de tetiklenebildiği için burada da kontrol et.
    if (mode === 'register' && !kvkk) {
      setNotice(''); setErr('Devam etmek için aydınlatma metnini onaylamalısın.');
      return;
    }
    setErr(''); setNotice(''); setBusy(true);
    try {
      const path = mode === 'login' ? 'login' : 'register';
      const body = mode === 'login' ? { email, password: pass } : { email, password: pass, name };
      const res  = await fetch(`${API}/api/auth/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Bir hata oluştu.');
      onSuccess(data);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  // Google ID token'ını backend'e gönder.
  const handleGoogleCredential = async (credential) => {
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/google`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Google girişi başarısız.');
      onSuccess(data);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  // GSI butonunu yükle ve render et.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    loadGoogleScript().then(() => {
      if (cancelled || !googleBtnRef.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => resp?.credential && handleGoogleCredential(resp.credential),
      });
      googleBtnRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black', size: 'large', shape: 'pill',
        text: 'continue_with', width: 300, locale: 'tr',
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  return (
    <div className="alt-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>
        <button className="alt-modal-close quota-close" onClick={onClose}>✕</button>
        <div className="auth-tabs">
          <button className={`auth-tab ${mode==='login'?'on':''}`}    onClick={() => switchMode('login')}>Giriş Yap</button>
          <button className={`auth-tab ${mode==='register'?'on':''}`} onClick={() => switchMode('register')}>Kayıt Ol</button>
        </div>

        {GOOGLE_CLIENT_ID && (
          <>
            <div className="google-btn-wrap" ref={googleBtnRef} />
            <div className="auth-divider"><span>veya</span></div>
          </>
        )}

        {mode==='register' && (
          <input className="auth-input" placeholder="Adın (isteğe bağlı)" value={name}
            onChange={e => setName(e.target.value)} />
        )}
        <input className="auth-input" type="email" placeholder="E-posta" value={email}
          autoCapitalize="none" autoCorrect="off"
          onChange={e => setEmail(e.target.value)} />
        <input className="auth-input" type="password" placeholder="Şifre (min. 6 karakter)" value={pass}
          onChange={e => setPass(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !busy && submit()} />

        {mode==='login' && (
          <button type="button" className="auth-forgot"
            onClick={() => { setErr(''); setNotice('Bu özellik yakında geliyor.'); }}>
            Şifremi unuttum?
          </button>
        )}

        {mode==='register' && (
          <label className="auth-kvkk">
            <input type="checkbox" checked={kvkk} onChange={e => setKvkk(e.target.checked)} />
            <span>
              Kişisel verilerimin işlenmesine ilişkin{' '}
              <a href={`${process.env.PUBLIC_URL || ''}/gizlilik.html`} target="_blank" rel="noopener noreferrer">Aydınlatma Metni</a>
              ’ni okudum ve onaylıyorum.
            </span>
          </label>
        )}

        {err && <p className="auth-err">{err}</p>}
        {notice && <p className="auth-notice">{notice}</p>}

        <button className="btn-primary full" onClick={submit}
          disabled={busy || !email || !pass || (mode==='register' && !kvkk)}>
          {busy ? <Spin /> : (mode==='login' ? 'Giriş Yap' : 'Hesap Oluştur')}
        </button>
        <p className="auth-switch">
          {mode==='login' ? 'Hesabın yok mu? ' : 'Zaten üye misin? '}
          <button onClick={() => switchMode(mode==='login'?'register':'login')}>
            {mode==='login' ? 'Kayıt ol' : 'Giriş yap'}
          </button>
        </p>
      </div>
    </div>
  );
}

function Header({ step, plan, onWeightClick, auth, onAuthClick, dashboard, onPremiumClick }) {
  const labels = ['Profil','Ürün','Market','Kalori','Plan'];
  const initial = auth ? (auth.user?.name?.[0] || auth.user?.email?.[0] || '?').toUpperCase() : null;
  const isPremium = auth?.user?.plan === 'premium';
  return (
    <header className={`hdr ${dashboard ? 'hdr-dash' : ''}`}>
      <div className="hdr-top">
        <div className="hdr-logo">
          <span>🥗</span>
          <div><strong>Markilo</strong><small>Bütçene uygun haftalık plan</small></div>
        </div>
        <div className="hdr-actions">
          {!dashboard && plan && (
            <button className="hdr-weight-btn" onClick={onWeightClick} title="Kilo Takibi">
              ⚖️ Kilo
            </button>
          )}
          {onPremiumClick && !isPremium && (
            <button className="hdr-premium-btn" onClick={onPremiumClick} title="Markilo Premium">
              ⭐ Premium
            </button>
          )}
          {auth
            ? <button className="hdr-avatar" onClick={onAuthClick} title={auth.user?.email}>{initial}</button>
            : <button className="hdr-login-btn" onClick={onAuthClick}>Giriş Yap</button>}
        </div>
      </div>
      {!dashboard && (
        <nav className="stepbar">
          {labels.map((lbl,i) => {
            const n=i+1, state=n<step?'done':n===step?'active':'idle';
            return (
              <div key={n} className="sb-item">
                <div className={`sb-dot ${state}`}>{state==='done'?'✓':n}</div>
                <span className={`sb-lbl ${state}`}>{lbl}</span>
                {i<4 && <div className={`sb-line ${n<step?'filled':''}`} />}
              </div>
            );
          })}
        </nav>
      )}
    </header>
  );
}

function Screen({ children, keyStr }) {
  return (
    <motion.section
      className="screen"
      key={keyStr}
      initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      exit={{ opacity: 0, y: -20, filter: 'blur(10px)' }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.section>
  );
}
function FieldLabel({ children }) { return <p className="f-lbl">{children}</p>; }

function NumInput({ label, unit, val, onChange, ph }) {
  return (
    <div className="num-input">
      <label className="ni-label">{label}</label>
      <div className="ni-wrap">
        <input type="number" value={val} placeholder={ph} onChange={e => onChange(e.target.value)} />
        <span className="ni-unit">{unit}</span>
      </div>
    </div>
  );
}

/* ── Plan Loading Screen ────────────────────────────────────── */
function PlanLoadingScreen() {
  const [idx, setIdx]         = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setIdx(i => (i+1)%HEALTH_FACTS.length); setVisible(true); }, 600);
    }, 7800);
    return () => clearInterval(id);
  }, []);
  const fact = HEALTH_FACTS[idx];
  return (
    <div className="plan-loading">
      <div className="pl-logo-spin">
        <div className="pl-ring" />
        <img src={`${process.env.PUBLIC_URL || ''}/markilo-icon.png`} alt="Markilo" className="pl-logo" />
      </div>
      <div className="pl-title">Plan Oluşturuluyor</div>
      <div className="pl-sub">Yapay zeka senin için çalışıyor…</div>
      <div className={`pl-fact-card ${visible?'show':''}`}>
        <div className="pl-fact-icon">{fact.icon}</div>
        <div className="pl-fact-text">{fact.text}</div>
      </div>
      <div className="pl-progress">
        {HEALTH_FACTS.map((_,i) => <div key={i} className={`pl-dot ${i===idx?'active':''}`} />)}
      </div>
      <div className="pl-counter">{idx+1} / {HEALTH_FACTS.length}</div>
    </div>
  );
}

/* ── Day Donut — saf SVG (Recharts yok, taşma sorunu çözüldü) ─── */
function DayDonut({ eaten, total, size = 50 }) {
  const pct  = total > 0 ? Math.min(eaten / total, 1) : 0;
  const over = eaten > total;

  const cx  = size / 2;
  const cy  = size / 2;
  const R   = size * 0.36;          // halka merkez yarıçapı
  const sw  = size * 0.16;          // halka kalınlığı
  const C   = 2 * Math.PI * R;      // çevre uzunluğu

  const arcColor = over ? '#ef4444' : '#22c55e';
  const filled   = (over ? 1 : pct) * C;

  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Arka plan halkası */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--s3, #1c2235)" strokeWidth={sw} />
        {/* Dolgu yayı — saat 12'den başlar, tik atınca yumuşak dolma animasyonu */}
        <circle cx={cx} cy={cy} r={R} fill="none"
          stroke={arcColor} strokeWidth={sw} strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={C - filled}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition:'stroke-dashoffset 0.7s cubic-bezier(0.34,1.56,0.64,1), stroke 0.3s ease' }}
        />
      </svg>
      {/* Merkez metin — SVG ile tam hizalı */}
      <svg width={size} height={size}
        style={{ position:'absolute', top:0, left:0, pointerEvents:'none' }}>
        {pct >= 1 && !over ? (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
            fontSize={size * 0.28} fontWeight="700" fill="#22c55e">✓</text>
        ) : over ? (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
            fontSize={size * 0.18} fontWeight="800" fill="#ef4444">MAX</text>
        ) : (
          <>
            <text x={cx} y={cy - size * 0.04} textAnchor="middle" dominantBaseline="central"
              fontSize={size * 0.2} fontWeight="700"
              fill={eaten > 0 ? 'var(--text, #f1f5f9)' : 'var(--t3, #475569)'}>
              {Math.round(eaten)}
            </text>
            <text x={cx} y={cy + size * 0.16} textAnchor="middle" dominantBaseline="central"
              fontSize={size * 0.15} fill="var(--t3, #475569)">
              /{total}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

/* ── DayCard ─────────────────────────────────────────────────── */
function DayCard({ day, di, open, onToggle, eatenMap, eatenKcal, targetKcal, onSetEaten, onAltClick, onItemClick, onCopy, onWhatsapp, onCopyDay, onRecipe }) {
  const over = eatenKcal > targetKcal;

  return (
    <div className={`day-card ${open?'open':''}`}>

      {/* ── Accordion başlığı ── */}
      <button className="dc-header" onClick={onToggle}>
        <div className="dc-left">
          <span className="dc-num">{di+1}</span>
          <div>
            <span className="dc-name">{DAYS_TR[di]||`${di+1}. Gün`}</span>
            <span className="dc-sub">{day.daily_calories} kcal · {day.daily_cost_tl} ₺</span>
          </div>
        </div>

        {/* Donut + opsiyonel limit etiketi */}
        <div className="dc-donut-wrap">
          <DayDonut eaten={eatenKcal} total={targetKcal} size={50} />
          {over && <span className="dc-over-lbl">!</span>}
        </div>

        <span className="dc-arrow">{open?'▲':'▼'}</span>
      </button>

      {/* ── Açık gövde ── */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="dc-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            {day.meals?.map((meal,mi) => (
            <div key={mi} className="meal-block">
              <div className="mb-header">
                <span className="mb-name">{meal.meal_name}</span>
                <span className="mb-stats">{meal.total_calories} kcal · {meal.total_protein_g}g pro · {meal.total_cost_tl} ₺</span>
                {onRecipe && (meal.items||[]).length > 0 && (
                  <button className="recipe-btn" onClick={() => onRecipe(meal)} title="Nasıl yapılır?">👨‍🍳</button>
                )}
              </div>
              <ul className="item-list">
                {(meal.items||[]).map((item,ii) => {
                  const state = eatenMap[`${di}-${mi}-${ii}`];
                  const ate   = state === true;
                  const skip  = state === false;
                  return (
                    <li key={ii} className={`item-row ${ate?'eaten':skip?'skipped':''}`}>
                      {/* Ürün adı + meta — sol */}
                      <div className="item-row-main">
                        <div className="item-info">
                          <span
                            className={`item-name ${ate?'strikethrough':''} ${onItemClick?'item-name-clickable':''}`}
                            onClick={onItemClick ? (e) => { e.stopPropagation(); onItemClick(item); } : undefined}
                          >
                            {item.product_name}
                            {item.is_alternative && <span className="alt-badge">ALT</span>}
                            {onItemClick && <span className="item-info-ico">ℹ️</span>}
                          </span>
                          <div className="item-meta">
                            <span className="item-g">{item.amount_g}g</span>
                            <span className="mkt-pill">{item.market}</span>
                            <div className="item-nums">
                              <span>{item.calories}kcal</span>
                              <span className="item-p">{item.protein_g}g p</span>
                              <span className="item-c">{item.carbs_g}g k</span>
                              <span className="item-f">{item.fat_g}g y</span>
                            </div>
                          </div>
                        </div>

                        {/* ✅ / ❌ butonları — sağ */}
                        <div className="item-actions">
                          <button
                            className={`eat-yes ${ate?'active':''}`}
                            onClick={() => onSetEaten(di,mi,ii,true)}
                            title="Yedim">✅</button>
                          <button
                            className={`eat-no ${skip?'active':''}`}
                            onClick={() => onSetEaten(di,mi,ii,false)}
                            title="Yemedim">❌</button>
                        </div>
                      </div>

                      {/* Alternatif üret — alt */}
                      <button className="alt-btn" onClick={() => onAltClick(di,mi,ii,item)}>
                        ↩ Alternatif üret
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Gün özeti çubuğu */}
          <div className={`day-summary ${over?'over':''}`}>
            <span>Yenilen: <strong>{eatenKcal} kcal</strong></span>
            <span>Hedef: <strong>{targetKcal} kcal</strong></span>
            {over
              ? <span className="ds-over">⚠️ +{eatenKcal-targetKcal} kcal fazla</span>
              : <span className="ds-rem">Kalan: {targetKcal-eatenKcal} kcal</span>}
          </div>

          {/* Paylaş butonu satırı */}
          <div className="share-row">
            {onCopyDay && (
              <button className="share-btn" onClick={() => onCopyDay(di)}>
                🗓️ Günü Kopyala
              </button>
            )}
            <button className="share-btn" onClick={() => onCopy(day, di)}>
              📋 Kopyala
            </button>
            <button className="share-btn whatsapp" onClick={() => onWhatsapp(day, di)}>
              💬 WhatsApp
            </button>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Alternative Modal ──────────────────────────────────────── */
function AlternativeModal({ altModal, altStep, altResult, altTargetMeal, meals, onSelectMeal, onConfirm, onClose }) {
  if (!altModal) return null;
  const { item } = altModal;
  return (
    <div className="alt-overlay" onClick={onClose}>
      <div className="alt-modal" onClick={e => e.stopPropagation()}>
        <div className="alt-modal-header">
          <span className="alt-modal-title">↩ Alternatif Üret</span>
          <button className="alt-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="alt-skipped">
          <span className="alt-skip-lbl">Atlanan ürün:</span>
          <span className="alt-skip-name">{item.product_name}</span>
          <span className="alt-skip-kcal">{item.calories} kcal · {item.protein_g}g pro</span>
        </div>

        {altStep === 'choose' && (
          <>
            <p className="alt-ask">Bu öğünün alternatifi hangi öğüne eklensin?</p>
            <div className="alt-meal-btns">
              {meals.map((m, mi) => (
                <button key={mi} className="alt-meal-btn"
                  onClick={() => onSelectMeal(m.meal_name, mi)}>
                  {m.meal_name}
                </button>
              ))}
            </div>
          </>
        )}

        {altStep === 'loading' && (
          <div className="alt-loading">
            <Spin />
            <span>"{altTargetMeal}" için alternatif aranıyor…</span>
          </div>
        )}

        {altStep === 'error' && (
          <div className="alt-error">Alternatif bulunamadı. Tekrar deneyin.</div>
        )}

        {altStep === 'result' && altResult && (
          <>
            <p className="alt-result-lbl">"{altTargetMeal}" için öneriler:</p>
            <div className="alt-results">
              {(altResult.alternatives||[]).map((a,i) => (
                <div key={i} className="alt-item">
                  <div className="alt-item-info">
                    <span className="alt-item-name">{a.product_name}</span>
                    <span className="alt-item-market">{a.market}</span>
                    <div className="alt-item-nums">
                      <span>{a.amount_g}g</span>
                      <span>{a.calories} kcal</span>
                      <span>{a.protein_g}g pro</span>
                      <span>{a.price_tl?.toFixed(2)} ₺</span>
                    </div>
                    {a.reason && <span className="alt-item-reason">{a.reason}</span>}
                  </div>
                  <button className="alt-item-add" onClick={() => onConfirm(a)}>
                    ✓ Ekle
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Weight Tracking Tab (Recharts) ─────────────────────────── */
function WeightTrackingTab({ startWeight, targetWeight: initialTarget, onRegenerate, onWeightUpdate, auth }) {
  const todayISO = new Date().toISOString().split('T')[0];

  const [log, setLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem('diyet_weight_log')||'[]'); } catch { return []; }
  });

  // Sunucudaki kilo geçmişiyle birleştir (cihazlar arası). Sunucuda olup lokalde
  // olmayan günleri ekler; çakışan günlerde lokali korur. Best-effort.
  useEffect(() => {
    if (!auth?.token) return;
    fetch(`${API}/api/weight-logs`, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        if (!Array.isArray(rows) || rows.length === 0) return;
        setLog(prev => {
          const byISO = new Map(prev.map(w => [w.dateISO, w]));
          for (const r of rows) {
            if (byISO.has(r.logged_at)) continue;
            const d = new Date(r.logged_at + 'T12:00:00');
            byISO.set(r.logged_at, {
              date: d.toDateString(),
              dateStr: d.toLocaleDateString('tr-TR', { day:'2-digit', month:'2-digit', year:'numeric' }),
              dateISO: r.logged_at, weight: r.weight_kg,
            });
          }
          const merged = Array.from(byISO.values())
            .sort((a, b) => new Date(b.dateISO) - new Date(a.dateISO)).slice(0, 60);
          try { localStorage.setItem('diyet_weight_log', JSON.stringify(merged)); } catch {}
          return merged;
        });
      })
      .catch(() => {});
  }, [auth]); // eslint-disable-line

  // Tek bir kilo kaydını sunucuya yaz (giriş yapılıysa). Best-effort.
  const pushWeight = (dateISO, kg) => {
    if (!auth?.token) return;
    fetch(`${API}/api/weight-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
      body: JSON.stringify({ weight_kg: kg, logged_at: dateISO }),
    }).catch(() => {});
  };
  const [dateInput,   setDateInput]   = useState(todayISO);
  const [weightInput, setWeightInput] = useState('');
  const [goalModal,   setGoalModal]   = useState(null);
  const [milestone,   setMilestone]   = useState(null);
  const [editKey,     setEditKey]     = useState(null);
  const [editDate,    setEditDate]    = useState('');
  const [editWeight,  setEditWeight]  = useState('');
  const [target,      setTarget]      = useState(() => {
    if (initialTarget) return String(initialTarget);
    return localStorage.getItem('diyet_target_weight')||'';
  });

  const saveLog = (l) => { localStorage.setItem('diyet_weight_log', JSON.stringify(l)); setLog(l); };

  // Başlangıç → girilen kiloya göre hedefe ulaşıldı mı / 2 kg'dan az mı kaldı?
  const checkGoal = (newWeight) => {
    const tgt   = parseFloat(target) || null;
    const start = startWeight || log[log.length-1]?.weight || null;
    if (!tgt || !start) return;
    const losing   = start > tgt;
    const remaining = Math.abs(newWeight - tgt);
    const reached  = losing ? newWeight <= tgt : newWeight >= tgt;
    if (reached || remaining <= 2) setGoalModal({ weight:newWeight });
  };

  // #16 — Ara kilometre taşları (başlangıçtan ±2/±5/±10/±15/±20 kg).
  const checkMilestone = (newWeight) => {
    const start = startWeight || log[log.length-1]?.weight || null;
    if (!start) return;
    const delta = Math.round((newWeight - start) * 10) / 10;
    const absD = Math.abs(delta);
    const steps = [2, 5, 10, 15, 20];
    const crossed = steps.filter(s => absD >= s).pop();
    if (!crossed) return;
    let reached;
    try { reached = JSON.parse(localStorage.getItem('diyet_milestones') || '[]'); } catch { reached = []; }
    const key = `${delta < 0 ? '-' : '+'}${crossed}`;
    if (reached.includes(key)) return;
    reached.push(key);
    try { localStorage.setItem('diyet_milestones', JSON.stringify(reached)); } catch {}
    setMilestone({ kg: crossed, dir: delta < 0 ? 'verdin' : 'aldın', total: absD });
  };

  const addEntry = () => {
    const kg = parseFloat(weightInput);
    if (!kg||kg<20||kg>400) return;
    const dateStr = new Date(dateInput+'T12:00:00').toLocaleDateString('tr-TR',{ day:'2-digit', month:'2-digit', year:'numeric' });
    const newLog = [
      { date:new Date(dateInput+'T12:00:00').toDateString(), dateStr, dateISO:dateInput, weight:kg },
      ...log.filter(w => w.dateISO !== dateInput),
    ].sort((a,b) => new Date(b.dateISO)-new Date(a.dateISO)).slice(0,60);
    saveLog(newLog); setWeightInput('');
    pushWeight(dateInput, kg);
    onWeightUpdate && onWeightUpdate(newLog[0].weight);
    checkMilestone(kg);
    checkGoal(kg);
  };

  const saveTarget = () => {
    if (target) localStorage.setItem('diyet_target_weight', target);
  };

  const startEdit = (w) => {
    setEditKey(w.dateISO || w.date);
    setEditDate(w.dateISO || todayISO);
    setEditWeight(String(w.weight));
  };
  const cancelEdit = () => { setEditKey(null); setEditDate(''); setEditWeight(''); };
  const saveEdit = (originalKey) => {
    const kg = parseFloat(editWeight);
    if (!kg||kg<20||kg>400||!editDate) return;
    const dateStr = new Date(editDate+'T12:00:00').toLocaleDateString('tr-TR',{ day:'2-digit', month:'2-digit', year:'numeric' });
    const entry = { date:new Date(editDate+'T12:00:00').toDateString(), dateStr, dateISO:editDate, weight:kg };
    const newLog = [
      entry,
      ...log.filter(w => (w.dateISO||w.date) !== originalKey && w.dateISO !== editDate),
    ].sort((a,b) => new Date(b.dateISO)-new Date(a.dateISO)).slice(0,60);
    saveLog(newLog);
    pushWeight(editDate, kg);
    onWeightUpdate && onWeightUpdate(newLog[0].weight);
    cancelEdit();
  };

  const chartData = [...log].reverse().map(w => ({ date:w.dateStr, kilo:w.weight }));
  const currentW  = log[0]?.weight;
  const firstW    = startWeight || log[log.length-1]?.weight;
  const tgt       = parseFloat(target)||null;
  const diff      = currentW && firstW ? Math.round((currentW-firstW)*10)/10 : null;

  return (
    <div className="weight-tab">
      {milestone && (
        <div className="alt-overlay" onClick={() => setMilestone(null)}>
          <div className="quota-modal goal-modal" onClick={e => e.stopPropagation()}>
            <div className="goal-modal-icon">🏆</div>
            <div className="goal-modal-title">{milestone.total} kg {milestone.dir}!</div>
            <div className="goal-modal-sub">
              {milestone.kg} kg'lık kilometre taşını geçtin. Emeklerinin karşılığını alıyorsun, devam et! 💪
            </div>
            <div className="goal-modal-btns">
              <button className="goal-btn-yes" onClick={() => setMilestone(null)}>Harika! 🎉</button>
            </div>
          </div>
        </div>
      )}
      {goalModal && (
        <div className="alt-overlay" onClick={() => setGoalModal(null)}>
          <div className="quota-modal goal-modal" onClick={e => e.stopPropagation()}>
            <div className="goal-modal-icon">🎉</div>
            <div className="goal-modal-title">{goalModal.weight} kg'a ulaştın, harika ilerleme!</div>
            <div className="goal-modal-sub">Yeni kilona göre kalori ve besin hedeflerin değişmeli.</div>
            <div className="goal-modal-btns">
              <button className="goal-btn-yes" onClick={() => { const w = goalModal.weight; setGoalModal(null); onRegenerate && onRegenerate(w); }}>
                Evet, yeni plan oluştur
              </button>
              <button className="goal-btn-no" onClick={() => setGoalModal(null)}>
                Hayır, devam edeceğim
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats kartları */}
      <div className="wt-stats">
        <div className="wts-card">
          <span className="wts-lbl">Başlangıç</span>
          <span className="wts-val">{firstW ? `${firstW} kg` : '—'}</span>
        </div>
        <div className="wts-card current">
          <span className="wts-lbl">Şu an</span>
          <span className="wts-val">{currentW ? `${currentW} kg` : '—'}</span>
        </div>
        <div className="wts-card">
          <span className="wts-lbl">Hedef</span>
          <span className="wts-val">{tgt ? `${tgt} kg` : '—'}</span>
        </div>
        <div className="wts-card">
          <span className="wts-lbl">Fark</span>
          <span className={`wts-val ${diff===null?'':diff<0?'green':'red'}`}>
            {diff===null ? '—' : `${diff>0?'+':''}${diff} kg`}
          </span>
        </div>
      </div>

      {/* Giriş formu */}
      <div className="wt-form">
        <input className="wtf-date" type="date" value={dateInput}
          onChange={e => setDateInput(e.target.value)} max={todayISO} />
        <input className="wtf-kg" type="number" placeholder="kg" value={weightInput}
          onChange={e => setWeightInput(e.target.value)}
          onKeyDown={e => e.key==='Enter' && addEntry()} />
        <button className="wtf-save" onClick={addEntry}>Kaydet</button>
      </div>

      <div className="wt-form" style={{ marginTop:8 }}>
        <input className="wtf-kg" type="number" placeholder="Hedef kilo (kg)"
          value={target} onChange={e => setTarget(e.target.value)} style={{ flex:1 }} />
        <button className="wtf-save" onClick={saveTarget} style={{ background:'#3b82f6' }}>Hedefi Kaydet</button>
      </div>

      {/* Recharts line chart */}
      {chartData.length >= 2 ? (
        <div className="wt-chart">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top:10, right:10, left:-20, bottom:5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2d47" />
              <XAxis dataKey="date" stroke="#475569" tick={{ fontSize:9, fill:'#94a3b8' }} />
              <YAxis stroke="#475569" tick={{ fontSize:9, fill:'#94a3b8' }} domain={['auto','auto']} />
              <Tooltip
                contentStyle={{ background:'#0f1421', border:'1px solid #1e2d47', borderRadius:8, fontSize:12 }}
                labelStyle={{ color:'#94a3b8' }}
                itemStyle={{ color:'#22c55e' }}
                formatter={(v) => [`${v} kg`, 'Kilo']}
              />
              <Line type="monotone" dataKey="kilo" stroke="#22c55e" strokeWidth={2.5}
                dot={{ fill:'#22c55e', r:4, strokeWidth:0 }}
                activeDot={{ r:6, fill:'#4ade80' }} />
              {tgt && (
                <ReferenceLine y={tgt} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={1.5}
                  label={{ value:`Hedef ${tgt}kg`, fill:'#ef4444', fontSize:9, position:'insideTopRight' }} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="wt-empty">
          📊 En az 2 kilo girişi yaptıktan sonra grafik görünecek
        </div>
      )}

      {/* Geçmiş */}
      {log.length > 0 && (
        <div className="wt-history">
          <div className="wth-title">Son Girişler</div>
          {log.slice(0,10).map((w,i) => {
            const key = w.dateISO||w.date;
            if (editKey === key) {
              return (
                <div key={key} className="wth-row wth-row-edit">
                  <input className="wth-edit-date" type="date" value={editDate} max={todayISO}
                    onChange={e => setEditDate(e.target.value)} />
                  <input className="wth-edit-kg" type="number" placeholder="kg" value={editWeight}
                    onChange={e => setEditWeight(e.target.value)}
                    onKeyDown={e => e.key==='Enter' && saveEdit(key)} />
                  <button className="wth-save" onClick={() => saveEdit(key)}>✓</button>
                  <button className="wth-cancel" onClick={cancelEdit}>✕</button>
                </div>
              );
            }
            const prev = log[i+1];
            const diff = prev ? Math.round((w.weight-prev.weight)*10)/10 : null;
            return (
              <div key={key} className="wth-row">
                <span className="wth-date">{w.dateStr}</span>
                <span className="wth-val">{w.weight} kg</span>
                {diff!==null && (
                  <span className={`wth-diff ${diff<0?'down':diff>0?'up':''}`}>
                    {diff>0?'+':''}{diff} kg
                  </span>
                )}
                <button className="wth-edit" onClick={() => startEdit(w)}>✏️</button>
                <button className="wth-del" onClick={() => saveLog(log.filter((_,j)=>j!==i))}>✕</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── PDF Bileşenleri ─────────────────────────────────────────── */
function PdfDropZone({ dragging, onDragOver, onDragLeave, onDrop, onClick }) {
  return (
    <div className={`pdf-zone ${dragging?'dragging':''}`}
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} onClick={onClick}>
      <div className="pdf-zone-icon">📄</div>
      <div className="pdf-zone-title">PDF'i buraya sürükle veya tıkla</div>
      <div className="pdf-zone-sub">Diyetisyen programı · Maks 20 MB</div>
    </div>
  );
}
function DotsLoader() { return <div className="dots-loader"><span /><span /><span /></div>; }

function PdfResults({ result, calorieChoice, onCalorieChoice, onReset, onConfirm }) {
  const { found_products=[], not_found_products=[], calorie_warning, dietitian_calories, total_calories } = result;
  return (
    <div className="pdf-result-section">
      <div className="pdf-summary-banner">
        <span>🩺 <strong>{found_products.length}</strong> ürün bulundu</span>
        {not_found_products.length>0&&<span>· <strong>{not_found_products.length}</strong> sorunlu</span>}
        {dietitian_calories&&<span>· Hedef <strong>{dietitian_calories} kcal</strong></span>}
      </div>
      {calorie_warning && (
        <div className="calorie-warn">
          <div className="cw-title">⚠️ Kalori Farkı Tespit Edildi</div>
          <div className="cw-msg">
            Diyetisyeniniz <strong>{calorie_warning.dietitian_calories} kcal</strong> önermiş,
            sizin hedefiniz <strong>{calorie_warning.user_calories} kcal</strong>
            {' '}({calorie_warning.direction==='more'?'+':'−'}{calorie_warning.difference} kcal).<br />Hangisine göre oluşturalım?
          </div>
          <div className="cw-btns">
            <button className={`cw-btn ${calorieChoice==='user'?'selected-user':''}`} onClick={() => onCalorieChoice('user')}>
              {calorieChoice==='user'?'✓ ':''}Kendi Hedefimle ({calorie_warning.user_calories} kcal)
            </button>
            <button className={`cw-btn ${calorieChoice==='dietitian'?'selected-diet':''}`} onClick={() => onCalorieChoice('dietitian')}>
              {calorieChoice==='dietitian'?'✓ ':''}Diyetisyenin Önerisiyle ({calorie_warning.dietitian_calories} kcal)
            </button>
          </div>
        </div>
      )}
      {found_products.length>0&&(
        <>
          <div className="pdf-group-label">✅ Seçtiğin marketlerde bulundu</div>
          <div className="pdf-cards">
            {found_products.map((p,i)=>(
              <div key={i} className="pdf-card found" style={{ animationDelay:`${i*40}ms` }}>
                <span className="pdf-card-icon">✅</span>
                <div className="pdf-card-info"><div className="pdf-card-name">{p.name}</div>
                  <div className="pdf-card-detail">{p.amount_g}g · {p.calories} kcal · {p.protein_g}g protein{p.meal_type&&p.meal_type!=='genel'&&<> · {p.meal_type}</>}</div></div>
                <div className="pdf-card-right"><span className="pdf-card-market">{p.market}</span><span className="pdf-card-price">{p.price_tl?.toFixed(2)} ₺</span></div>
              </div>
            ))}
          </div>
        </>
      )}
      {not_found_products.length>0&&(
        <>
          <div className="pdf-group-label" style={{ marginTop:14 }}>
            {not_found_products.some(p=>p.status==='alternative_market')?'⚠️':'❌'} Bulunamayan / Alternatif
          </div>
          <div className="pdf-cards">
            {not_found_products.map((p,i)=>(
              <div key={i} className={`pdf-card ${p.status==='alternative_market'?'alt':'missing'}`} style={{ animationDelay:`${i*40}ms` }}>
                <span className="pdf-card-icon">{p.status==='alternative_market'?'⚠️':'❌'}</span>
                <div className="pdf-card-info"><div className="pdf-card-name">{p.requested_name}</div>
                  <div className="pdf-card-detail">{p.status==='alternative_market'?`"${p.product_name}" → ${p.found_in}`:'Bulunamadı'}</div></div>
              </div>
            ))}
          </div>
        </>
      )}
      {found_products.length>0&&(
        <div className="pdf-total-row"><span>Toplam:</span><span className="pdf-total-kcal">{total_calories} kcal/gün</span></div>
      )}
      <div className="pdf-confirm-row">
        <button className="pdf-reset-btn" onClick={onReset}>✕ İptal</button>
        <button className="btn-primary" onClick={onConfirm}
          disabled={found_products.length===0||(!!calorie_warning&&!calorieChoice)}>
          {calorie_warning&&!calorieChoice?'Önce kalori tercihi yap':`${found_products.length} ürünü onayla →`}
        </button>
      </div>
    </div>
  );
}

/* ── Locked Card ────────────────────────────────────────────── */
function LockedCard({ lp, dailyKcal, onChangeAmt, onRemove, animDelay }) {
  const pct         = dailyKcal ? Math.round((lp.calories/dailyKcal)*100) : 0;
  const protein_pct = Math.round((lp.protein_g*4/Math.max(lp.calories,1))*100);
  const carbs_pct   = Math.round((lp.carbs_g  *4/Math.max(lp.calories,1))*100);
  const fat_pct     = Math.round((lp.fat_g    *9/Math.max(lp.calories,1))*100);
  return (
    <div className="locked-card" style={{ animationDelay:`${animDelay}ms` }}>
      <div className="lc-top"><span className="lc-name">{lp.name}</span><button className="lc-remove" onClick={onRemove}>✕</button></div>
      <div className="lc-gram-row">
        <label className="lc-gram-lbl">Miktar</label>
        <div className="lc-gram-inp">
          <input type="number" value={lp.amount_g} min={1} max={2000} onChange={e=>onChangeAmt(e.target.value)} />
          <span>g</span>
        </div>
        <span className="lc-kcal">{lp.calories} kcal</span>
      </div>
      <div className="lc-chart-row">
        <DonutChart segments={[{pct:protein_pct,color:'#22c55e'},{pct:carbs_pct,color:'#3b82f6'},{pct:fat_pct,color:'#f59e0b'}]} />
        <div className="lc-macros">
          <MacroLine label="Protein" val={lp.protein_g} color="#22c55e" />
          <MacroLine label="Karb"    val={lp.carbs_g}   color="#3b82f6" />
          <MacroLine label="Yağ"     val={lp.fat_g}     color="#f59e0b" />
        </div>
        {dailyKcal>0&&<div className="lc-pct-badge"><span>{pct}%</span><small>günlük</small></div>}
      </div>
    </div>
  );
}
function MacroLine({ label, val, color }) {
  return <div className="ml-row"><span className="ml-dot" style={{ background:color }} /><span className="ml-label">{label}</span><span className="ml-val">{val}g</span></div>;
}
function DonutChart({ segments }) {
  const R=40,CIRC=2*Math.PI*R;
  const [ready,setReady]=useState(false);
  useEffect(()=>{ const t=setTimeout(()=>setReady(true),50); return ()=>clearTimeout(t); },[]);
  let cum=0; const total=segments.reduce((s,sg)=>s+sg.pct,0)||1;
  return (
    <svg className="donut" viewBox="-55 -55 110 110">
      <circle r={R} fill="none" stroke="var(--s3, #1c2235)" strokeWidth={14} />
      {segments.map((sg,i)=>{const ratio=sg.pct/total,dash=ready?ratio*CIRC:0,rot=-90+(cum/total)*360;cum+=sg.pct;
        return <circle key={i} r={R} fill="none" stroke={sg.color} strokeWidth={14} strokeDasharray={`${dash} ${CIRC}`} transform={`rotate(${rot})`} style={{ transition:'stroke-dasharray 0.8s ease' }} />;
      })}
    </svg>
  );
}
function AnimNum({ target, big }) {
  const [val,setVal]=useState(0);
  useEffect(()=>{
    let cur=0; const end=target||0,step=end/(900/16);
    const t=setInterval(()=>{ cur=Math.min(cur+step,end); setVal(Math.round(cur)); if(cur>=end) clearInterval(t); },16);
    return ()=>clearInterval(t);
  },[target]);
  return <span className={big?'ch-val-big':'ch-val'}>{val.toLocaleString()}</span>;
}

/* ── Ayarlar: düzenlenebilir profil kartı (kilo/hedef kilo/aktivite) ── */
function ProfileEditCard({ form, bmi, streak, onSave }) {
  const wStr = form.weight_kg ? String(form.weight_kg) : '';
  const twStr = form.target_weight_kg ? String(form.target_weight_kg) : '';
  const aStr = form.activity_level || 'moderate';

  const [weight,   setWeight]   = useState(wStr);
  const [targetW,  setTargetW]  = useState(twStr);
  const [activity, setActivity] = useState(aStr);
  const [busy,     setBusy]     = useState(false);

  // Dışarıdan (örn. Kilo sekmesi) profil değişirse input'ları senkronla.
  useEffect(() => { setWeight(wStr); setTargetW(twStr); setActivity(aStr); }, [wStr, twStr, aStr]);

  const dirty = weight !== wStr || targetW !== twStr || activity !== aStr;

  const handleSave = async () => {
    setBusy(true);
    await onSave({ weight_kg: weight, target_weight_kg: targetW, activity_level: activity });
    setBusy(false);
  };

  return (
    <div className="set-card">
      <h3 className="set-card-title">Profil</h3>
      <div className="set-row"><span>Yaş</span><span>{form.age || '—'}</span></div>
      <div className="set-row"><span>Boy</span><span>{form.height_cm ? `${form.height_cm} cm` : '—'}</span></div>

      <div className="set-edit-row">
        <label htmlFor="set-weight">Kilo</label>
        <div className="set-edit-field">
          <input id="set-weight" type="number" inputMode="decimal" min={30} max={300}
            value={weight} onChange={e => setWeight(e.target.value)} />
          <span>kg</span>
        </div>
      </div>
      <div className="set-edit-row">
        <label htmlFor="set-target">Hedef Kilo</label>
        <div className="set-edit-field">
          <input id="set-target" type="number" inputMode="decimal" min={30} max={300}
            value={targetW} onChange={e => setTargetW(e.target.value)} />
          <span>kg</span>
        </div>
      </div>
      <div className="set-edit-row">
        <label htmlFor="set-activity">Aktivite</label>
        <select id="set-activity" className="set-edit-select"
          value={activity} onChange={e => setActivity(e.target.value)}>
          {ACTIVITIES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>

      <div className="set-row"><span>Hedef</span><span>{GOAL_LABELS[form.goal] || form.goal}</span></div>
      {bmi && (
        <div className="set-row">
          <span>VKİ (BMI)</span>
          <span style={{ color: bmi.color, fontWeight: 700 }}>{bmi.bmi} — {bmi.cat}</span>
        </div>
      )}
      <div className="set-row"><span>🔥 Plan Serisi</span><span style={{ color:'#f59e0b', fontWeight:700 }}>{streak} gün</span></div>

      <button className="btn-primary full" style={{ marginTop: 14 }} onClick={handleSave} disabled={!dirty || busy}>
        {busy ? <Spin /> : '💾 Değişiklikleri Kaydet'}
      </button>
    </div>
  );
}

function MacroRow({ mc, val, pct, delay, isMax }) {
  const [filled,setFilled]=useState(false);
  useEffect(()=>{ const t=setTimeout(()=>setFilled(true),delay+100); return ()=>clearTimeout(t); },[delay]);
  return (
    <div className="macro-row">
      <div className="mr-top">
        <span className="mr-icon">{mc.icon}</span><span className="mr-label">{mc.label}</span>
        <span className="mr-val" style={{ color:mc.color }}>{val}{mc.unit}</span>
        {isMax&&<span className="mr-max-tag">maks</span>}
      </div>
      <div className="mr-track"><div className="mr-fill" style={{ width:filled?`${pct}%`:'0%', background:mc.color }} /></div>
    </div>
  );
}
function PlanStat({ val, lbl, unit }) {
  return <div className="ph-stat"><span className="ph-val">{val}</span>{unit&&<span className="ph-unit">{unit}</span>}<span className="ph-lbl">{lbl}</span></div>;
}
function ShoppingList({ items }) {
  const list = items || [];
  const keyOf = (it) => `${it.market || 'Diğer'}|${it.product}`;
  // İşaretlenen (alınan) ürünler — markette gezerken işaretle, kalıcı saklanır.
  const [checked, setChecked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('diyet_shop_checked') || '[]')); }
    catch { return new Set(); }
  });
  const toggle = (it) => setChecked(prev => {
    const next = new Set(prev);
    const k = keyOf(it);
    if (next.has(k)) next.delete(k); else next.add(k);
    try { localStorage.setItem('diyet_shop_checked', JSON.stringify([...next])); } catch {}
    return next;
  });

  const groups = {};
  list.forEach(item => { const m = item.market || 'Diğer'; if (!groups[m]) groups[m] = []; groups[m].push(item); });
  const mktColor = Object.fromEntries(MARKETS.map(m => [m.id, m.color]));

  const grand = list.reduce((s, i) => s + (i.price_tl || 0), 0);
  const bought = list.filter(i => checked.has(keyOf(i)));
  const remaining = list.filter(i => !checked.has(keyOf(i))).reduce((s, i) => s + (i.price_tl || 0), 0);
  const pct = list.length ? Math.round(bought.length / list.length * 100) : 0;

  return (
    <div className="shop-groups">
      {list.length > 0 && (
        <div className="shop-summary">
          <div className="shop-sum-top">
            <span className="shop-sum-lbl">Toplam Sepet</span>
            <span className="shop-sum-grand">{grand.toFixed(2)} ₺</span>
          </div>
          <div className="shop-sum-bar"><div className="shop-sum-fill" style={{ width: `${pct}%` }} /></div>
          <div className="shop-sum-meta">
            <span>✓ {bought.length}/{list.length} alındı</span>
            <span>Kalan: <strong>{remaining.toFixed(2)} ₺</strong></span>
          </div>
        </div>
      )}
      {Object.entries(groups).map(([market, mItems]) => (
        <div key={market} className="shop-group">
          <div className="sg-header">
            <span className="sg-dot" style={{ background: mktColor[market] || '#6b7280' }} />
            <span className="sg-name">{market}</span>
            <span className="sg-total">{mItems.reduce((s, i) => s + (i.price_tl || 0), 0).toFixed(2)} ₺</span>
          </div>
          {mItems.map((item, i) => {
            const on = checked.has(keyOf(item));
            return (
              <button key={i} className={`shop-item ${on ? 'bought' : ''}`} onClick={() => toggle(item)}>
                <span className={`si-check ${on ? 'on' : ''}`}>{on ? '✓' : ''}</span>
                <span className="si-name">{item.product}</span>
                <span className="si-qty">{item.quantity}</span>
                <span className="si-price">{item.price_tl} ₺</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
function Spin() { return <span className="spinner" />; }
function Err({ msg }) { return <div className="err-box">{msg}</div>; }

/* ── Ürün Sağlık Skoru rozeti (Yuka tarzı) ────────────────────── */
function HealthScore({ data, size = 'sm', showReasons = false }) {
  if (!data) return null;
  return (
    <span className={`hscore-wrap hscore-${size}`}>
      <span className="hscore" style={{ background: data.color }} title={`${data.label} · ${data.score}/100`}>
        <span className="hscore-grade">{data.grade}</span>
        {size !== 'sm' && <span className="hscore-num">{data.score}<small>/100</small></span>}
      </span>
      {showReasons && data.reasons?.length > 0 && (
        <span className="hscore-reasons">
          {data.reasons.map((r, i) => <span key={i} className="hscore-chip">{r}</span>)}
        </span>
      )}
    </span>
  );
}

// Tüm günlerin yemek günlüğünü tarayıp en sık eklenenleri döndür (hızlı tekrar ekleme için).
function getFrequentFoods(limit = 6) {
  const map = new Map();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('diyet_foodlog_')) continue;
      const arr = JSON.parse(localStorage.getItem(k) || '[]');
      for (const e of arr) {
        if (!e?.name) continue;
        const prev = map.get(e.name);
        if (prev) prev.count++;
        else map.set(e.name, {
          name: e.name, amount_g: e.amount_g, source: e.source || 'frequent',
          calories: e.calories, protein_g: e.protein_g, carbs_g: e.carbs_g, fat_g: e.fat_g, count: 1,
        });
      }
    }
  } catch {}
  return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, limit);
}

/* ── Water Tracker ──────────────────────────────────────────────── */
function WaterTracker({ count, target, onAdd, onRemove, onAdjustTarget, weekData }) {
  const [justFilled, setJustFilled] = useState(-1);
  const handleAdd = () => {
    if (count >= target) return;
    setJustFilled(count);
    onAdd();
    setTimeout(() => setJustFilled(-1), 500);
  };
  const fillPct = Math.round((count / target) * 100);
  const todayDow = (new Date().getDay() + 6) % 7;

  return (
    <div className="water-tab">
      <div className="water-hero" style={{ '--water-fill': `${fillPct}%` }}>
        <div className="water-icon">{count >= target ? '🌟' : '💧'}</div>
        <div>
          <span className="water-count">{count}</span>
          <span className="water-count-unit"> / {target}</span>
        </div>
        <div className="water-target">
          {count >= target
            ? <strong>Bugünkü hedefe ulaştın! 🎉</strong>
            : <>Hedefe <strong>{target - count} bardak</strong> kaldı</>}
        </div>
      </div>

      <div className="water-glasses">
        {Array.from({ length: target }, (_, i) => (
          <button key={i}
            className={`water-glass ${i < count ? 'filled' : ''} ${justFilled === i ? 'just-filled' : ''}`}
            onClick={i < count ? onRemove : handleAdd}>
            <span className="water-glass-icon">{i < count ? '💧' : '🥤'}</span>
            <span className="water-glass-num">{i + 1}</span>
          </button>
        ))}
      </div>

      <div className="water-adjust">
        <button className="water-adj-btn" onClick={() => onAdjustTarget(-1)}>−</button>
        <span className="water-adj-label">Günlük hedef: {target} bardak</span>
        <button className="water-adj-btn" onClick={() => onAdjustTarget(1)}>+</button>
      </div>

      <div className="water-weekly">
        <div className="water-weekly-title">📊 Bu Hafta</div>
        <div className="water-bars">
          {weekData.map((val, i) => {
            const h = target > 0 ? Math.round((val / target) * 100) : 0;
            return (
              <div key={i} className="water-bar-col">
                <div className="water-bar" style={{ height: '60px' }}>
                  <div className="water-bar-fill" style={{ height: `${Math.min(100, h)}%` }} />
                </div>
                <span className={`water-bar-day ${i === todayDow ? 'today' : ''}`}>
                  {WATER_DAYS_SHORT[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Weekly Progress Card ─────────────────────────────────────── */
/* ── Adaptif Hedef / Kilo Trendi İçgörü Kartı ─────────────────── */
function TrendInsightCard({ trend, onApply }) {
  if (!trend || trend.status === 'insufficient') return null;
  const cfg = ({
    on_track: { ic: '✅', color: '#22c55e', title: 'Hedefindesin' },
    plateau:  { ic: '📉', color: '#f59e0b', title: 'Durağanlık (Plato)' },
    too_fast: { ic: '⚠️', color: '#ef4444', title: 'Çok Hızlı İlerliyor' },
    drifting: { ic: '🎚️', color: '#f59e0b', title: 'Hedeften Sapma' },
  })[trend.status] || { ic: '📊', color: '#3b82f6', title: 'Kilo Trendi' };
  const arrow = trend.ratePerWeek < 0 ? '▼' : trend.ratePerWeek > 0 ? '▲' : '▬';

  return (
    <div className="trend-card" style={{ '--tc': cfg.color }}>
      <div className="trend-head">
        <span className="trend-ic">{cfg.ic}</span>
        <span className="trend-title">{cfg.title}</span>
        <span className="trend-rate">{arrow} {Math.abs(trend.ratePerWeek)} kg/hafta</span>
      </div>
      <p className="trend-msg">{trend.message}</p>
      {trend.suggestedTarget && (
        <button className="trend-apply" onClick={() => onApply(trend.suggestedTarget)}>
          <span>🎯 Kalori hedefini <strong>{trend.suggestedTarget} kcal</strong> yap</span>
          <small>{trend.suggestedDelta > 0 ? '+' : ''}{trend.suggestedDelta} kcal</small>
        </button>
      )}
      <div className="trend-foot">{trend.count} kilo kaydı · son {trend.daysSpan} gün analiz edildi</div>
    </div>
  );
}

function WeeklyProgressCard({ progress }) {
  const { eaten, skipped, total, pct } = progress;
  const tracked = eaten + skipped;
  if (tracked === 0) return null;

  const R = 35, sw = 8, C = 2 * Math.PI * R;
  const filled = (pct / 100) * C;
  const color = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
  const motiv = pct >= 80 ? '💪 Harika gidiyorsun! Planına sadık kalıyorsun.'
    : pct >= 50 ? '🙌 İyi gidiyorsun, biraz daha çaba hedefine ulaşmanı sağlar!'
    : '🌟 Her gün yeni bir başlangıç. Yarın daha iyi olacak!';

  return (
    <div className="progress-card">
      <div className="progress-card-title"><span>📊</span> Haftalık İlerleme</div>
      <div className="progress-ring-row">
        <svg className="progress-ring-svg" viewBox="0 0 90 90">
          <circle cx="45" cy="45" r={R} fill="none" stroke="var(--s3, #1c2235)" strokeWidth={sw} />
          <circle cx="45" cy="45" r={R} fill="none"
            stroke={color} strokeWidth={sw} strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C - filled}
            transform="rotate(-90 45 45)"
            style={{ transition:'stroke-dashoffset 1s cubic-bezier(0.34,1.56,0.64,1)' }}
          />
          <text x="45" y="42" textAnchor="middle" dominantBaseline="central"
            fontSize="18" fontWeight="800" fill={color}>{pct}%</text>
          <text x="45" y="58" textAnchor="middle" dominantBaseline="central"
            fontSize="9" fill="#94a3b8">uyum</text>
        </svg>
        <div className="progress-ring-info">
          <div className="progress-stat">
            <span className="progress-stat-label">✅ Yenilen</span>
            <span className="progress-stat-val green">{eaten} öğün</span>
          </div>
          <div className="progress-stat">
            <span className="progress-stat-label">❌ Atlanan</span>
            <span className="progress-stat-val">{skipped} öğün</span>
          </div>
          <div className="progress-stat">
            <span className="progress-stat-label">📝 Toplam</span>
            <span className="progress-stat-val">{total} öğün</span>
          </div>
        </div>
      </div>
      <div className="progress-motiv">
        <span>{pct >= 80 ? '🌟' : pct >= 50 ? '💪' : '✨'}</span>
        {motiv}
      </div>
    </div>
  );
}

/* ── Başarı Rozetleri (#15) ───────────────────────────────────── */
function BadgesSection({ badges }) {
  const earned = badges.filter(b => b.earned).length;
  return (
    <div className="set-card">
      <h3 className="set-card-title">🏅 Rozetler <span className="badge-count">{earned}/{badges.length}</span></h3>
      <div className="badge-grid">
        {badges.map(b => (
          <div key={b.id} className={`badge ${b.earned ? 'earned' : 'locked'}`} title={b.desc}>
            <span className="badge-ic">{b.earned ? b.ic : '🔒'}</span>
            <span className="badge-title">{b.title}</span>
            <span className="badge-desc">{b.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Saved Plans Section ──────────────────────────────────────── */
function SavedPlansSection({ plans, onLoad }) {
  return (
    <div className="saved-plans">
      <div className="saved-plans-title">📁 Geçmiş Planlar</div>
      {plans.length === 0 ? (
        <div className="saved-plans-empty">Henüz geçmiş plan yok</div>
      ) : (
        plans.map(plan => (
          <button key={plan.id} className="saved-plan-card" onClick={() => onLoad(plan)}>
            <div className="saved-plan-icon">📅</div>
            <div className="saved-plan-info">
              <div className="saved-plan-date">{plan.date}</div>
              <div className="saved-plan-meta">
                {plan.calories} kcal/gün · {plan.cost} ₺ · {GOAL_LABELS[plan.goal] || plan.goal}
              </div>
            </div>
            <span className="saved-plan-arrow">›</span>
          </button>
        ))
      )}
    </div>
  );
}

/* ── Fiyat Tazeliği Rozeti ────────────────────────────────────── */
function PriceFreshnessBadge({ data }) {
  if (!data || data.days_ago === null || data.days_ago === undefined) return null;
  const d = data.days_ago;
  const fresh = d <= 7;
  const label = d === 0 ? 'bugün' : d === 1 ? 'dün' : `${d} gün önce`;
  return (
    <div className={`price-fresh ${fresh ? 'fresh' : 'stale'}`}>
      <span className="pf-dot" />
      🏷️ Fiyatlar en son <strong>{label}</strong> güncellendi
      {!fresh && <span className="pf-note"> · güncel fiyat için markete bakın</span>}
    </div>
  );
}

/* ── Günlük Yemek Günlüğü Kartı ───────────────────────────────── */
function FoodLogCard({ log, totals, targetKcal, targetPro, targetMacros, onOpen, onRemove }) {
  const kcalPct = targetKcal ? Math.min(100, Math.round((totals.kcal / targetKcal) * 100)) : 0;
  const proPct  = targetPro  ? Math.min(100, Math.round((totals.protein / targetPro) * 100)) : 0;
  const remaining = targetKcal ? Math.max(0, Math.round(targetKcal - totals.kcal)) : null;
  const over = targetKcal && totals.kcal > targetKcal;

  // Kalan makrolar (hedef varsa)
  const macroBars = targetMacros ? [
    { key:'protein', lbl:'Protein', eaten: totals.protein, target: targetMacros.protein_g, color:'#22c55e' },
    { key:'carbs',   lbl:'Karb',    eaten: totals.carbs,   target: targetMacros.carbs_g,   color:'#3b82f6' },
    { key:'fat',     lbl:'Yağ',     eaten: totals.fat,     target: targetMacros.fat_g,     color:'#f59e0b' },
    ...(targetMacros.fiber_g ? [{ key:'fiber', lbl:'Lif', eaten: totals.fiber || 0, target: targetMacros.fiber_g, color:'#a3a3a3' }] : []),
  ] : [];

  return (
    <div className="foodlog-card">
      <div className="foodlog-head">
        <div className="foodlog-title"><span>🍽️</span> Bugün Yedikleri</div>
        <button className="foodlog-add-btn" onClick={onOpen}>+ Ekle</button>
      </div>

      <div className="foodlog-totals">
        <div className="flt-main">
          <span className={`flt-kcal ${over ? 'over' : ''}`}>{Math.round(totals.kcal)}</span>
          <span className="flt-kcal-unit">/ {targetKcal || '—'} kcal</span>
        </div>
        <div className="flt-bar"><div className="flt-bar-fill" style={{ width: `${kcalPct}%` }} /></div>
        {remaining !== null && (
          <div className="flt-rem">
            {over ? <span className="flt-over">⚠️ +{Math.round(totals.kcal - targetKcal)} kcal fazla</span>
                  : <span>Kalan: <strong>{remaining} kcal</strong></span>}
          </div>
        )}
        {macroBars.length > 0 ? (
          <div className="flt-macrobars">
            {macroBars.map(m => {
              const eaten = Math.round(m.eaten);
              const target = Math.round(m.target || 0);
              const pct = target ? Math.min(100, Math.round((eaten / target) * 100)) : 0;
              const rem = Math.max(0, target - eaten);
              return (
                <div key={m.key} className="fmb">
                  <div className="fmb-head">
                    <span className="fmb-lbl">{m.lbl}</span>
                    <span className="fmb-val">{eaten}<span className="fmb-tg">/{target}g</span></span>
                  </div>
                  <div className="fmb-track"><div className="fmb-fill" style={{ width:`${pct}%`, background:m.color }} /></div>
                  <div className="fmb-rem">{rem}g kaldı</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flt-macros">
            <span className="flt-macro"><i style={{ background:'#22c55e' }} /> P {Math.round(totals.protein)}g{targetPro ? ` (%${proPct})` : ''}</span>
            <span className="flt-macro"><i style={{ background:'#3b82f6' }} /> K {Math.round(totals.carbs)}g</span>
            <span className="flt-macro"><i style={{ background:'#f59e0b' }} /> Y {Math.round(totals.fat)}g</span>
          </div>
        )}
        {(totals.sugar > 0 || totals.sodium > 0) && (
          <div className="flt-limits">
            <span className={`flt-limit ${targetMacros?.sugar_g && totals.sugar > targetMacros.sugar_g ? 'over' : ''}`}>
              🍬 Şeker {Math.round(totals.sugar)}g{targetMacros?.sugar_g ? ` / ${targetMacros.sugar_g}g` : ''}
            </span>
            <span className={`flt-limit ${targetMacros?.sodium_g && totals.sodium > targetMacros.sodium_g * 1000 ? 'over' : ''}`}>
              🧂 Sodyum {Math.round(totals.sodium)}mg{targetMacros?.sodium_g ? ` / ${Math.round(targetMacros.sodium_g * 1000)}mg` : ''}
            </span>
          </div>
        )}
      </div>

      {log.length === 0 ? (
        <div className="foodlog-empty">Henüz bir şey eklemedin. Barkod okut, ara veya elle ekle.</div>
      ) : (
        <div className="foodlog-items">
          {log.map(f => (
            <div key={f.ts} className="foodlog-item">
              <div className="fli-info">
                <span className="fli-name">{f.name}</span>
                <span className="fli-meta">{f.amount_g}g · {f.calories} kcal · {f.protein_g}g pro
                  {f.source === 'off' && <span className="fli-src">OFF</span>}
                  {f.source === 'manual' && <span className="fli-src">elle</span>}
                </span>
              </div>
              <button className="fli-del" onClick={() => onRemove(f.ts)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Yemek Ekleme Modalı (barkod / arama / manuel) ────────────── */
function FoodLogModal({ onAdd, onClose }) {
  const [tab, setTab] = useState('search'); // 'search' | 'barcode' | 'manual'
  const [picked, setPicked] = useState(null); // { name, brand, source, per100:{...} }
  const [amount, setAmount] = useState(100);

  // Arama
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  // Barkod
  const [code, setCode] = useState('');
  const [bcBusy, setBcBusy] = useState(false);
  const [bcErr, setBcErr] = useState('');
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const supportsScan = typeof window !== 'undefined' && 'BarcodeDetector' in window
    && (navigator.mediaDevices?.getUserMedia);

  // Manuel
  const [mName, setMName] = useState('');
  const [mKcal, setMKcal] = useState('');
  const [mPro,  setMPro]  = useState('');
  const [mCarb, setMCarb] = useState('');
  const [mFat,  setMFat]  = useState('');

  // Fotoğraf (Claude vision)
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr,  setPhotoErr]  = useState('');
  const photoInputRef = useRef(null);
  const analyzePhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true); setPhotoErr('');
    try {
      const fd = new FormData(); fd.append('photo', file);
      const r = await fetch(`${API}/api/analyze-food-photo`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Tanınamadı.');
      const amt = data.amount_g || 100;
      const f = (amt / 100) || 1;
      setPicked({
        name: data.name, brand: data.confidence < 0.5 ? 'tahmini' : null, source: 'photo',
        per100: {
          calories:  Math.round((data.calories  || 0) / f),
          protein_g: Math.round((data.protein_g || 0) / f * 10) / 10,
          carbs_g:   Math.round((data.carbs_g   || 0) / f * 10) / 10,
          fat_g:     Math.round((data.fat_g     || 0) / f * 10) / 10,
        },
      });
      setAmount(amt);
    } catch (e) { setPhotoErr(e.message || 'Tanınamadı.'); }
    setPhotoBusy(false);
  };

  const doSearch = (val) => {
    setQ(val); clearTimeout(searchTimer.current);
    if (val.length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${API}/api/products/search?q=${encodeURIComponent(val)}`);
        setResults(await r.json());
      } catch { setResults([]); }
      setSearching(false);
    }, 350);
  };

  const pickFromSearch = (p) => {
    const n = p.nutritions || {};
    setPicked({ name: p.name, brand: p.brand, source: 'db', health: p.health,
      per100: { calories: n.calories||0, protein_g: n.protein_g||0, carbs_g: n.carbs_g||0, fat_g: n.fat_g||0, fiber_g: n.fiber_g||0, sugar_g: n.sugar_g||0, sodium_mg: n.sodium_mg||0 } });
    setAmount(100);
  };

  // Sık yenenler — tek tuşla aynı kaydı tekrar günlüğe ekle.
  const frequent = (q.length < 2 && tab === 'search' && !picked) ? getFrequentFoods(6) : [];
  const quickAdd = (f) => onAdd({
    name: f.name, amount_g: f.amount_g, source: f.source,
    calories: f.calories, protein_g: f.protein_g, carbs_g: f.carbs_g, fat_g: f.fat_g,
  });

  const lookupBarcode = async (raw) => {
    const c = (raw || '').replace(/\D/g, '');
    if (c.length < 6) { setBcErr('Geçerli bir barkod girin.'); return; }
    setBcBusy(true); setBcErr('');
    try {
      const r = await fetch(`${API}/api/barcode/${c}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Bulunamadı.');
      setPicked({ name: data.name, brand: data.brand, source: data.source, health: data.health,
        per100: { calories: data.calories||0, protein_g: data.protein_g||0, carbs_g: data.carbs_g||0, fat_g: data.fat_g||0, fiber_g: data.fiber_g||0, sugar_g: data.sugar_g||0, sodium_mg: data.sodium_mg||0 } });
      setAmount(data.serving_g || 100);
    } catch (e) { setBcErr(e.message || 'Bulunamadı.'); }
    setBcBusy(false);
  };

  // Kamera ile tarama (BarcodeDetector destekliyorsa)
  const stopScan = () => {
    setScanning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };
  const startScan = async () => {
    if (!supportsScan) return;
    setBcErr('');
    try {
      const detector = new window.BarcodeDetector({ formats: ['ean_13','ean_8','upc_a','upc_e','code_128'] });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setScanning(true);
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(()=>{}); }
      const tick = async () => {
        if (!videoRef.current || !streamRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length) {
            const found = codes[0].rawValue;
            stopScan(); setCode(found); lookupBarcode(found); return;
          }
        } catch { /* kare atla */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch { setBcErr('Kameraya erişilemedi. Barkodu elle girin.'); setScanning(false); }
  };
  useEffect(() => () => stopScan(), []); // unmount → kamerayı kapat

  const addPicked = () => {
    if (!picked) return;
    const g = Math.max(1, Math.min(3000, Number(amount) || 100));
    const f = g / 100;
    onAdd({
      name: picked.name + (picked.brand ? ` (${picked.brand})` : ''),
      amount_g: g, source: picked.source,
      calories:  Math.round(picked.per100.calories * f),
      protein_g: Math.round(picked.per100.protein_g * f * 10) / 10,
      carbs_g:   Math.round(picked.per100.carbs_g * f * 10) / 10,
      fat_g:     Math.round(picked.per100.fat_g * f * 10) / 10,
      fiber_g:   Math.round((picked.per100.fiber_g || 0) * f * 10) / 10,
      sugar_g:   Math.round((picked.per100.sugar_g || 0) * f * 10) / 10,
      sodium_mg: Math.round((picked.per100.sodium_mg || 0) * f),
    });
  };

  const addManual = () => {
    const kcal = parseInt(mKcal, 10);
    if (!mName.trim() || !Number.isFinite(kcal) || kcal <= 0) return;
    const g = Math.max(1, Math.min(3000, Number(amount) || 100));
    onAdd({
      name: mName.trim(), amount_g: g, source: 'manual',
      calories: kcal,
      protein_g: parseFloat(mPro) || 0,
      carbs_g:   parseFloat(mCarb) || 0,
      fat_g:     parseFloat(mFat) || 0,
    });
  };

  const switchTab = (t) => { setTab(t); setPicked(null); setBcErr(''); setPhotoErr(''); if (t !== 'barcode') stopScan(); };
  const preview = picked ? {
    f: (Number(amount) || 100) / 100,
  } : null;

  return (
    <div className="alt-overlay" onClick={() => { stopScan(); onClose(); }}>
      <div className="food-modal" onClick={e => e.stopPropagation()}>
        <div className="alt-modal-header">
          <span className="alt-modal-title">🍽️ Yemek Ekle</span>
          <button className="alt-modal-close" onClick={() => { stopScan(); onClose(); }}>✕</button>
        </div>

        <div className="food-tabs">
          {[['search','🔍 Ara'],['photo','📸 Foto'],['barcode','📷 Barkod'],['manual','✏️ Elle']].map(([k,lb]) => (
            <button key={k} className={`food-tab ${tab===k?'on':''}`} onClick={() => switchTab(k)}>{lb}</button>
          ))}
        </div>

        {/* Seçilen ürün → miktar + ekle */}
        {picked && (
          <div className="food-picked">
            <div className="fp-name">{picked.name}{picked.brand && <small> · {picked.brand}</small>}</div>
            {picked.health && (
              <div className="fp-health"><HealthScore data={picked.health} size="lg" showReasons /></div>
            )}
            <div className="fp-amount">
              <label>Miktar</label>
              <input type="number" value={amount} min={1} max={3000}
                onChange={e => setAmount(e.target.value)} />
              <span>g</span>
            </div>
            <div className="portion-chips">
              {[30, 50, 100, 150, 200, 250].map(g => (
                <button key={g} className={`portion-chip ${Number(amount) === g ? 'on' : ''}`}
                  onClick={() => setAmount(g)}>{g}g</button>
              ))}
            </div>
            <div className="fp-preview">
              {Math.round(picked.per100.calories * preview.f)} kcal ·
              {' '}{Math.round(picked.per100.protein_g * preview.f * 10) / 10}g pro ·
              {' '}{Math.round(picked.per100.carbs_g * preview.f * 10) / 10}g karb ·
              {' '}{Math.round(picked.per100.fat_g * preview.f * 10) / 10}g yağ
            </div>
            <div className="fp-actions">
              <button className="btn-ghost" onClick={() => setPicked(null)}>← Geri</button>
              <button className="btn-primary" onClick={addPicked}>✓ Günlüğe Ekle</button>
            </div>
          </div>
        )}

        {/* ARAMA */}
        {!picked && tab === 'search' && (
          <>
            <div className="search-wrap">
              <span className="search-icon">🔍</span>
              <input className="search-inp" type="text" autoFocus
                placeholder="Ürün ara... (ör: yoğurt, muz)"
                value={q} onChange={e => doSearch(e.target.value)} />
              {searching && <Spin />}
            </div>
            {results.length > 0 && (
              <div className="search-results">
                {results.map(p => (
                  <button key={p.id} className="search-result-item" onClick={() => pickFromSearch(p)}>
                    {p.health && <HealthScore data={p.health} />}
                    <div className="sri-info"><strong>{p.name}</strong>
                      <small>{p.nutritions?.calories} kcal / 100g · {p.nutritions?.protein_g}g protein</small></div>
                    <span className="add-ico">+</span>
                  </button>
                ))}
              </div>
            )}
            {frequent.length > 0 && (
              <div className="freq-foods">
                <div className="freq-title">⏱️ Sık Eklediklerin</div>
                <div className="freq-chips">
                  {frequent.map((f, i) => (
                    <button key={i} className="freq-chip" onClick={() => quickAdd(f)}>
                      <span className="freq-chip-name">{f.name}</span>
                      <span className="freq-chip-kcal">{f.calories} kcal</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {q.length >= 2 && !searching && results.length === 0 && (
              <div className="food-hint">Sonuç yok — Barkod veya Elle sekmesini dene.</div>
            )}
          </>
        )}

        {/* FOTOĞRAF */}
        {!picked && tab === 'photo' && (
          <div className="food-photo">
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) analyzePhoto(f); e.target.value = ''; }} />
            {photoBusy ? (
              <div className="photo-loading"><Spin /> Yemek analiz ediliyor…</div>
            ) : (
              <button className="photo-drop" onClick={() => photoInputRef.current?.click()}>
                <span className="photo-ic">📸</span>
                <span className="photo-cta">Yemeğin fotoğrafını çek veya seç</span>
                <span className="photo-sub">Yapay zeka porsiyonu ve kaloriyi tahmin etsin</span>
              </button>
            )}
            {photoErr && <p className="food-err">{photoErr}</p>}
            <p className="food-hint">📸 Tahmini değerlerdir — gerekirse miktarı düzeltebilirsin.</p>
          </div>
        )}

        {/* BARKOD */}
        {!picked && tab === 'barcode' && (
          <div className="food-barcode">
            {supportsScan && (
              <>
                {!scanning ? (
                  <button className="btn-secondary full" onClick={startScan}>📷 Kamerayı Aç ve Tara</button>
                ) : (
                  <div className="bc-camera">
                    <video ref={videoRef} playsInline muted className="bc-video" />
                    <div className="bc-scanline" />
                    <button className="bc-stop" onClick={stopScan}>Durdur</button>
                  </div>
                )}
                <div className="food-or">veya barkodu elle gir</div>
              </>
            )}
            <div className="search-wrap">
              <span className="search-icon">#️⃣</span>
              <input className="search-inp" type="tel" inputMode="numeric"
                placeholder="Barkod numarası" value={code}
                onChange={e => setCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && lookupBarcode(code)} />
            </div>
            <button className="btn-primary full" onClick={() => lookupBarcode(code)} disabled={bcBusy}>
              {bcBusy ? <Spin /> : 'Barkodu Ara'}
            </button>
            {bcErr && <p className="food-err">{bcErr}</p>}
            {!supportsScan && <p className="food-hint">📷 Kamera taraması bu cihazda desteklenmiyor — barkodu elle girebilirsin.</p>}
          </div>
        )}

        {/* MANUEL */}
        {!picked && tab === 'manual' && (
          <div className="food-manual">
            <input className="auth-input" placeholder="Yemek adı (ör: Ev yapımı çorba)"
              value={mName} onChange={e => setMName(e.target.value)} />
            <div className="fm-row">
              <div className="fm-field"><label>Miktar (g)</label>
                <input type="number" value={amount} min={1} onChange={e => setAmount(e.target.value)} /></div>
              <div className="fm-field"><label>Kalori (toplam)</label>
                <input type="number" value={mKcal} onChange={e => setMKcal(e.target.value)} placeholder="kcal" /></div>
            </div>
            <div className="fm-row">
              <div className="fm-field"><label>Protein (g)</label>
                <input type="number" value={mPro} onChange={e => setMPro(e.target.value)} placeholder="0" /></div>
              <div className="fm-field"><label>Karb (g)</label>
                <input type="number" value={mCarb} onChange={e => setMCarb(e.target.value)} placeholder="0" /></div>
              <div className="fm-field"><label>Yağ (g)</label>
                <input type="number" value={mFat} onChange={e => setMFat(e.target.value)} placeholder="0" /></div>
            </div>
            <button className="btn-primary full" onClick={addManual} disabled={!mName.trim() || !mKcal}>
              ✓ Günlüğe Ekle
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Besin Detay Modalı (Fiyat Karşılaştırma dahil) ─────────────── */
function NutritionDetailModal({ item, onClose, onDislike }) {
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item?.product_name) return;
    setLoading(true);
    fetch(`${API}/api/products/prices?q=${encodeURIComponent(item.product_name)}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setPrices(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [item?.product_name]);

  if (!item) return null;
  const g = item.amount_g || 100;
  const MACROS = [
    { label:'Kalori',       val: item.calories,   unit:'kcal', color:'#f59e0b', max:800 },
    { label:'Protein',      val: item.protein_g,  unit:'g',    color:'#22c55e', max:100 },
    { label:'Karbonhidrat', val: item.carbs_g,    unit:'g',    color:'#3b82f6', max:200 },
    { label:'Yağ',          val: item.fat_g,      unit:'g',    color:'#a855f7', max:100 },
  ];

  // Ucuzdan pahalıya sırala — mevcut market'i vurgula
  const priceRows = prices?.[0]?.prices || [];
  const cheapest = priceRows[0];
  const health = prices?.[0]?.health;

  return (
    <div className="alt-overlay" onClick={onClose}>
      <div className="nd-modal" onClick={e => e.stopPropagation()}>
        <div className="alt-modal-header">
          <span className="alt-modal-title">🥗 Besin Detayı</span>
          <button className="alt-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Ürün adı + market */}
        <div className="nd-product">
          <div className="nd-name">{item.product_name}</div>
          <div className="nd-sub">
            <span className="mkt-pill">{item.market}</span>
            <span className="nd-gram">{g}g · {item.price_tl} ₺</span>
          </div>
          {health && <div className="nd-health"><HealthScore data={health} size="lg" showReasons /></div>}
        </div>

        {/* Makro çubukları */}
        <div className="nd-macros">
          {MACROS.map((m, i) => {
            const pct = Math.min(100, Math.round((m.val / m.max) * 100));
            return (
              <div key={i} className="nd-macro-row">
                <div className="nd-macro-head">
                  <span className="nd-macro-label">{m.label}</span>
                  <span className="nd-macro-val" style={{ color: m.color }}>
                    {m.val}{m.unit}
                  </span>
                </div>
                <div className="nd-macro-track">
                  <div className="nd-macro-fill"
                    style={{ width:`${pct}%`, background: m.color,
                      transition:'width 0.8s cubic-bezier(0.34,1.56,0.64,1)' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Fiyat karşılaştırması */}
        <div className="nd-prices-section">
          <div className="nd-prices-title">🏷️ Market Fiyatları</div>
          {loading ? (
            <div className="nd-loading"><Spin /> Fiyatlar yükleniyor…</div>
          ) : priceRows.length === 0 ? (
            <div className="nd-no-price">Bu ürün için fiyat karşılaştırması bulunamadı.</div>
          ) : (
            <div className="nd-price-rows">
              {priceRows.map((pr, i) => (
                <div key={i} className={`nd-price-row ${pr === cheapest ? 'cheapest' : ''}`}>
                  <span className="nd-mkt">{pr.market}</span>
                  {pr === cheapest && <span className="nd-best-badge">En Ucuz</span>}
                  <span className="nd-price-val"
                    style={{ color: pr === cheapest ? '#22c55e' : 'var(--text)' }}>
                    {pr.price_tl?.toFixed(2)} ₺
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 100g başına not */}
        <div className="nd-per100">
          <span>📊 100g başına: {Math.round(item.calories * 100 / g)} kcal
          · {Math.round(item.protein_g * 100 / g * 10) / 10}g protein</span>
        </div>

        {/* Şeker / sodyum (DB'de varsa) */}
        {(health && (prices?.[0]?.nutrition?.sugar_g != null || prices?.[0]?.nutrition?.sodium_mg != null)) && (
          <div className="nd-extra">
            {prices[0].nutrition.sugar_g != null && (
              <span className="nd-extra-pill">🍬 Şeker {prices[0].nutrition.sugar_g}g/100g</span>
            )}
            {prices[0].nutrition.sodium_mg != null && (
              <span className="nd-extra-pill">🧂 Sodyum {Math.round(prices[0].nutrition.sodium_mg)}mg/100g</span>
            )}
          </div>
        )}

        {onDislike && item.product_name && (
          <button className="nd-dislike" onClick={() => onDislike(item.product_name)}>
            🚫 Bu ürünü bir daha önerme
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Tarif (Hazırlanış) Modalı ────────────────────────────────── */
function RecipeModal({ data, onClose }) {
  return (
    <div className="alt-overlay" onClick={onClose}>
      <div className="nd-modal" onClick={e => e.stopPropagation()}>
        <div className="alt-modal-header">
          <span className="alt-modal-title">👨‍🍳 Nasıl Yapılır?</span>
          <button className="alt-modal-close" onClick={onClose}>✕</button>
        </div>
        {data.loading ? (
          <div className="nd-loading"><Spin /> Tarif hazırlanıyor…</div>
        ) : data.error || !data.data ? (
          <div className="nd-no-price">Tarif oluşturulamadı. Lütfen tekrar dene.</div>
        ) : (
          <div className="recipe-body">
            <div className="recipe-title">{data.data.title || data.mealName}</div>
            {data.data.prep_minutes && (
              <div className="recipe-time">⏱️ ~{data.data.prep_minutes} dakika</div>
            )}
            <ol className="recipe-steps">
              {(data.data.steps || []).map((s, i) => (
                <li key={i}><span className="recipe-step-no">{i + 1}</span><span>{s}</span></li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── PREMIUM SAYFASI (/premium) ──────────────────────────────────
   Referans: MacroFactor / FatSecret — temiz, ferah, değer odaklı,
   suçlayıcı olmayan, davetkar bir abonelik sayfası. Her zaman koyu tema. */
const PREMIUM_COMPARE = [
  { feature: 'Aylık plan hakkı',             free: '3',        prem: '30' },
  { feature: 'Alternatif öğün önerisi',      free: 'Sınırlı',  prem: 'Sınırsız' },
  { feature: 'Market fiyat karşılaştırması', free: true,       prem: true },
  { feature: 'Bütçe optimizasyonu',          free: true,       prem: true },
  { feature: 'Öncelikli destek',             free: false,      prem: true },
  { feature: 'Reklamsız deneyim',            free: true,       prem: true },
];

const PREMIUM_BENEFITS = [
  { icon: '🎯', title: 'Daha fazla plan', text: 'Her ay 30 farklı plan oluştur, çeşitliliği koru.' },
  { icon: '💰', title: 'Bütçeni koru',    text: 'Gerçek market fiyatlarıyla en uygun alışveriş.' },
  { icon: '⚡', title: 'Anında öneri',    text: 'Sınırsız alternatif öğün, takılırsan hemen değiştir.' },
];

const PREMIUM_FAQ = [
  { q: 'İstediğim zaman iptal edebilir miyim?',
    a: 'Evet, dilediğin zaman iptal edebilirsin. İptal sonrası dönem sonuna kadar Premium ayrıcalıkların devam eder.' },
  { q: 'Ödeme güvenli mi?',
    a: 'Ödemeler iyzico altyapısı ile 256-bit SSL şifreleme üzerinden korunur. Kart bilgilerin bizde saklanmaz.' },
  { q: 'Ücretsiz plan yeterli değil mi?',
    a: 'Ücretsiz plan ayda 3 plan sunar; çoğu kullanıcı için iyi bir başlangıçtır. Daha fazla çeşitlilik ve sınırsız öneri istersen Premium tam sana göre.' },
];

function PremiumCell({ value, premium }) {
  if (value === true)  return <span className={`pp-check ${premium ? 'on' : ''}`}>✓</span>;
  if (value === false) return <span className="pp-cross">✗</span>;
  return <span className={`pp-val ${premium ? 'on' : ''}`}>{value}</span>;
}

function PremiumPage({ auth, onUpgradeClick, onBack }) {
  const [openFaq, setOpenFaq] = useState(0);
  const logo = `${process.env.PUBLIC_URL || ''}/markilo-icon.png`;

  return (
    <div className="premium-page">
      <button className="pp-back" onClick={onBack} aria-label="Geri">‹ Geri</button>

      {/* 1 — HERO */}
      <section className="pp-hero">
        <img src={logo} alt="Markilo" className="pp-logo"
          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <h1 className="pp-title">Markilo Premium</h1>
        <p className="pp-subtitle">Bütçene uygun, sınırsız diyet planı</p>
      </section>

      {/* 2 — FİYAT KARTI */}
      <section className="pp-price-wrap">
        <div className="pp-price-card">
          <div className="pp-badge">EN POPÜLER</div>
          <div className="pp-price">
            <span className="pp-amount">100₺</span>
            <span className="pp-period">/ay</span>
          </div>
          <p className="pp-cancel">İstediğin zaman iptal et</p>
          <button className="pp-cta" onClick={onUpgradeClick}>Premium'a Geç</button>
        </div>
      </section>

      {/* 3 — KARŞILAŞTIRMA TABLOSU */}
      <section className="pp-section">
        <h2 className="pp-h2">Ücretsiz ile Premium</h2>
        <div className="pp-table">
          <div className="pp-row pp-row-head">
            <span className="pp-c-feature">Özellik</span>
            <span className="pp-c-free">Ücretsiz</span>
            <span className="pp-c-prem">Premium</span>
          </div>
          {PREMIUM_COMPARE.map((r) => (
            <div className="pp-row" key={r.feature}>
              <span className="pp-c-feature">{r.feature}</span>
              <span className="pp-c-free"><PremiumCell value={r.free} /></span>
              <span className="pp-c-prem"><PremiumCell value={r.prem} premium /></span>
            </div>
          ))}
        </div>
      </section>

      {/* 4 — DEĞER VURGUSU */}
      <section className="pp-section">
        <h2 className="pp-h2">Neler kazanırsın?</h2>
        <div className="pp-benefits">
          {PREMIUM_BENEFITS.map((b) => (
            <div className="pp-benefit" key={b.title}>
              <div className="pp-benefit-ic">{b.icon}</div>
              <strong className="pp-benefit-title">{b.title}</strong>
              <p className="pp-benefit-text">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5 — SSS */}
      <section className="pp-section">
        <h2 className="pp-h2">Sıkça sorulanlar</h2>
        <div className="pp-faq">
          {PREMIUM_FAQ.map((item, i) => (
            <div className={`pp-faq-item ${openFaq === i ? 'open' : ''}`} key={item.q}>
              <button className="pp-faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                <span>{item.q}</span>
                <span className="pp-faq-icon">{openFaq === i ? '−' : '+'}</span>
              </button>
              {openFaq === i && <p className="pp-faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* 6 — ALT CTA */}
      <section className="pp-section pp-final">
        <button className="pp-cta" onClick={onUpgradeClick}>Premium'a Geç</button>
        <p className="pp-support">
          Sorularınız için:{' '}
          <a href="mailto:destek@markilo.app">destek@markilo.app</a>
        </p>
        {auth?.user?.plan === 'premium' && (
          <p className="pp-already">⭐ Zaten Premium üyesin — teşekkürler!</p>
        )}
      </section>
    </div>
  );
}
